import React, { useState, useCallback, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { useFrontAuthStore } from '../../auth/store/authStore';
import DarkModeToggle from '../ui/DarkModeToggle';
import AnnotationsToggle from '../ui/AnnotationsToggle';
import Modal from '../ui/Modal';
import { useTheme } from '../../../core/contexts/ThemeContext';
import syncService, { SyncRoomData, SyncResolveData } from '../../services/syncService';
import useSyncSocket, { SyncRequest, SharedFolderPublishData } from '../../hooks/useSyncSocket';
import apiClient from '../../../core/http/axios';
import ProfessorNotesModal from '../ui/ProfessorNotesModal';
import AttachmentModal from '../attachment/AttachmentModal';
import MenuIcon from '../ui/MenuIcon';
import PdfViewer from '../attachment/PdfViewer';
import { Attachment } from '../../services/attachmentService';
import { useAttachmentContext } from '../../contexts/AttachmentContext';
import { useFontSettings, FontSettings } from '../../contexts/FontSettingsContext';
import {
  DEFAULT_FONT_SETTINGS,
  FONT_MODAL_RANGES,
  FONT_MODAL_SNAP_POINTS,
  lineHeightToValue,
  valueToLineHeight,
  fontWeightToValue,
  valueToFontWeight,
} from '../../types/settings';
import { getSettings, updateSettings as updateBookViewerSettings } from '../../services/settingsService';
import { SyncSocketProvider } from '../../contexts/SyncSocketContext';
import { setSyncRoomCookies, getSyncRoomIdCookie, getSyncJoinCodeCookie, deleteSyncRoomCookies } from '../../../core/utils/cookie';
import { quizImageService } from '../../services/quizImageService';

// 자간 변환 (0~20 → -0.1em~0.3em, 5=0em)
const letterSpacingToEm = (value: number) => ((value - 5) * 0.02);

// CSS 변수로 폰트 설정 적용 (Context에서 호출)
const applyFontSettings = (settings: FontSettings) => {
  const root = document.documentElement;
  
  // CSS 변수 설정
  root.style.setProperty('--viewer-font-family', settings.fontFamily === 'inherit' ? 'inherit' : `"${settings.fontFamily}", sans-serif`);
  root.style.setProperty('--viewer-font-size', `${settings.fontSize}px`);
  root.style.setProperty('--viewer-font-weight', `${settings.fontWeight}`);
  root.style.setProperty('--viewer-letter-spacing', `${letterSpacingToEm(settings.letterSpacing)}em`);
  root.style.setProperty('--viewer-line-height', `${settings.lineHeight}`);
};

const LayoutContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  min-height: 100vh;
  background-color: var(--bg-primary);
  transition: background-color 0.3s ease;
  padding: 0 ${props => props.theme.spacing.xl} ${props => props.theme.spacing.xl};
  pointer-events: auto;
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
  height: 72px;
`;

const HeaderContent = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.lg};
  width: 100%;
  height: 72px;
  padding: ${props => props.theme.spacing.lg} 0;
  pointer-events: none;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  pointer-events: auto;
`;

const Logo = styled.div<{ $isDark?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  cursor: pointer;
  & img {
    max-width: fit-content;
    height: 28px;
    ${props => props.$isDark && `filter: invert(1);`}
  }
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  width: 40px;
  height: 40px;
  padding: ${props => props.theme.spacing.xs};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: ${props => props.theme.borderRadius.full};
  background-color: transparent;
  outline: none;

  html.dark-mode & img {
    filter: invert(1) brightness(1);
  }

  &:hover {
    background-color: ${props => props.theme.colors.border.de};
  }
  
`;

const MenuDropdown = styled.div<{ $alignRight?: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  left: ${props => props.$alignRight ? 'auto' : '0'};
  right: ${props => props.$alignRight ? '0' : 'auto'};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.xs};
  background: var(--bg-primary, #fff);
  border: 1px solid ${props => props.theme.colors.border.de};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.10);
  min-width: 110px;
  z-index: 1000;
  overflow: hidden;
`;

const MenuDropdownItem = styled.button`
  width: 100%;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  text-align: center;
  background: transparent;
  color: ${props => props.theme.colors.book.color4b};
  font-size: ${props => props.theme.fontSize.xs};
  font-weight: ${props => props.theme.fontWeight.semibold};
  border-radius: ${props => props.theme.borderRadius.sm};
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background: ${props => props.theme.colors.gray[100]};
  }
`;

const MenuButtonContainer = styled.div`
  position: relative;
`;

const IconGroup = styled.div<{ $isCentered?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xl};
  pointer-events: auto;
  height: 40px;
  ${props => props.$isCentered 
    ? `
    `
    : `
      margin-left: auto;
      margin-right: ${props.theme.spacing.xl};
    `
  }
`;

const BookTitleSeparator = styled.span`
  width: 1px;
  height: 1em;
  background-color: ${props => props.theme.colors.gray[400]};
`;

const BookTitleText = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.medium};
  color: var(--text-secondary);
`;

const IconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: var(--icon-color);
  background-color: transparent;
  border-radius: ${props => props.theme.borderRadius.full};
  transition: all 0.2s ease;
  outline: none;
  
  svg {
    width: 20px;
    height: 20px;
  }

  html.dark-mode & img {
    filter: invert(1) brightness(1);
  }
  &:hover {
    background-color: ${props => props.theme.colors.border.de};
  }
`;

const iconPopBounce = keyframes`
  0% { transform: translateY(0); }
  25% { transform: translateY(-6px); }
  50% { transform: translateY(2px); }
  75% { transform: translateY(-2px); }
  100% { transform: translateY(0); }
`;

const AnimatedIconButton = styled(IconButton)<{ $pop?: boolean }>`
  ${props => props.$pop && css`
    animation: ${iconPopBounce} 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
  `}
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  pointer-events: auto;
`;

const LogoutButton = styled.button`
  color: var(--text-tertiary);
  font-size: ${props => props.theme.fontSize.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all 0.2s ease;
  background: none;
  border: 1px solid var(--border-primary);
  cursor: pointer;

  &:hover {
    color: ${props => props.theme.colors.error[600]};
    border-color: ${props => props.theme.colors.error[200]};
    background-color: ${props => props.theme.colors.error[50]};
  }
`;

const Main = styled.main`
  flex: 1;
  min-height: 0;
  overflow: hidden;
  pointer-events: auto;
  border-radius: ${props => props.theme.borderRadius.lg};
  background: ${props => props.theme.colors.white};
`;

// ============================================================================
// 탭 바 스타일
// ============================================================================
// ============================================================================
// 동기화 모달 스타일
// ============================================================================
const SyncCodeDisplay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.theme.spacing.xl};
`;

const SyncCode = styled.span`
  font-size: 48px;
  font-weight: ${props => props.theme.fontWeight.bold};
  letter-spacing: 12px;
  color: ${props => props.theme.colors.primary[600]};
  font-family: 'Courier New', monospace;
`;

const SyncCodeBoxGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
`;

const SyncCodeBox = styled.div`
  width: calc(100% / 4 - ${props => props.theme.spacing.md} / 4);
  height: 80px;
  text-align: center;
  font-size: ${props => props.theme.fontSize['5xl']};
  font-weight: ${props => props.theme.fontWeight.bold};
  border: 1px solid ${props => props.theme.colors.border.ce};
  border-radius: ${props => props.theme.borderRadius.lg};
  outline: none;
  transition: border-color 0.2s;
  background-color: ${props => props.theme.colors.border.f9};
  box-sizing: border-box;
  color: ${props => props.theme.colors.book.color0b};
  &:focus {
    border-color: ${props => props.theme.colors.primary[500]};
    background-color: ${props => props.theme.colors.white};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.book.color9b};
  }
`;

const SyncRegenerateLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.primary[600]};
  cursor: pointer;
  text-decoration: underline;
  margin-top: ${props => props.theme.spacing.sm};

  &:hover {
    color: ${props => props.theme.colors.primary[700]};
  }

  &:disabled {
    color: ${props => props.theme.colors.gray[400]};
    cursor: not-allowed;
    text-decoration: none;
  }
`;

const SyncModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
  padding-top: ${props => props.theme.spacing['3xl']};
`;

const SyncModalButton = styled.button<{ $primary?: boolean }>`
  min-width: 120px;
  width: calc(50% - ${props => props.theme.spacing.md} / 2);
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing['2xl']};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.medium};
  cursor: pointer;
  transition: background-color 0.2s, color 0.2s;

  ${props => props.$primary
    ? `
      background-color: ${props.theme.colors.book.color2b};
      color: ${props.theme.colors.white};
      border: none;

      &:hover {
        background-color: ${props.theme.colors.book.color1b};
      }

      &:disabled {
        background-color: ${props.theme.colors.border.ce};
        cursor: not-allowed;
      }
    `
    : `
      background-color: ${props.theme.colors.white};
      color: ${props.theme.colors.book.color6b};
      border: 1px solid ${props.theme.colors.border.ce};

      &:hover {
        background-color: ${props.theme.colors.border.f9};
      }
    `}
`;

const SyncCodeInputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  width: 100%;
`;

const SyncCodeInputGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
`;

const SyncCodeInput = styled.input`
  width: calc(100% / 4 - ${props => props.theme.spacing.md} / 4);
  height: 80px;
  text-align: center;
  font-size: ${props => props.theme.fontSize['5xl']};
  font-weight: ${props => props.theme.fontWeight.bold};
  border: 1px solid ${props => props.theme.colors.border.ce};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.sm};
  outline: none;
  transition: border-color 0.2s;
  background-color: ${props => props.theme.colors.border.f9};
  color: ${props => props.theme.colors.book.color0b};
  
  &:focus {
    border-color: ${props => props.theme.colors.primary[500]};
    background-color: ${props => props.theme.colors.white};
  }
  
  &::placeholder {
    color: ${props => props.theme.colors.book.color9b};
  }
`;

const SyncErrorMessage = styled.div`
  width: 100%;
  color: ${props => props.theme.colors.error[600]};
  font-size: ${props => props.theme.fontSize.sm};
  text-align: left;
`;

const SyncLoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => props.theme.spacing.xl};
  
  &::after {
    content: '';
    width: 40px;
    height: 40px;
    border: 4px solid ${props => props.theme.colors.gray[200]};
    border-top: 4px solid ${props => props.theme.colors.primary[600]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SyncSubmitButton = styled.button`
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  background-color: ${props => props.theme.colors.primary[600]};
  color: white;
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.medium};
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${props => props.theme.colors.primary[700]};
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.gray[400]};
    cursor: not-allowed;
  }
`;

const SyncDescription = styled.p`
  width: 100%;
  text-align: left;
  color: ${props => props.theme.colors.book.color6b};
  font-size: ${props => props.theme.fontSize.sm};
  margin-top: ${props => props.theme.spacing.xs};
`;

// ============================================================================
// 동기화 토글 스위치 스타일
// ============================================================================
const SyncButton = styled.button<{ $isOn: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
  width: 92px;
  height: 30px;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.$isOn ? props.theme.colors.white : props.theme.colors.border.ee};
  border-radius: 20px;
  background: ${props => props.$isOn
    ? 'linear-gradient(to right, #edfff6 0%, #d4ffd8 100%)'
    : 'linear-gradient(to right,#eee 0%, #999 100%)'};
  color: ${props => props.$isOn ? '#28c328' : '#666666'};
  font-size: ${props => props.theme.fontSize.sm};
  font-weight: ${props => props.theme.fontWeight.bold};
  cursor: pointer;
  outline: none;
  transition: all 0.2s ease;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${props => props.$isOn ? 'linear-gradient(to right, #E8ECEA 0%, #3bb346 100%)' : 'linear-gradient(to right, #FAFAFA 0%, #DBDBDB 100%)'};
    border-radius: 20px;
    opacity: 0.6;
    mix-blend-mode: color-burn;
  }

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    path {
      fill: ${props => props.$isOn ? '#00c116' : '#999999'};
    }
  }

  &:hover {
    opacity: 0.85;
  }

  &:active {
    transform: scale(0.97);
  }
`;

// 교수 퀵뷰 플로팅 버튼
const QuickViewFloatingButton = styled.button`
  position: fixed;
  right: 0;
  bottom: calc(25% + 68px);
  width: 32px;
  height: 60px;
  background-color: ${props => props.theme.colors.gray[800]};
  background: linear-gradient(148deg, rgba(0, 168, 62, 0.90) 10.08%, rgba(14, 14, 14, 0.90) 126.3%);
  border-radius: 6px 0 0 6px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${props => props.theme.shadows.lg};
  z-index: 1003;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${props => props.theme.colors.gray[700]};
  }

  &:focus {
    outline: none;
  }

  img {
    width: 18px;
    height: 18px;
    object-fit: contain;
    filter: brightness(0) invert(1);
  }
`;

// 동기화 해제 확인 모달 스타일
const SyncDisconnectModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SyncDisconnectModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 28px 32px 24px;
  min-width: 320px;
  max-width: 400px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  position: relative;
`;

const SyncDisconnectModalClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  font-size: 20px;
  line-height: 1;
  padding: 4px;

  &:hover {
    color: #333;
  }
`;

const SyncDisconnectModalTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const SyncDisconnectModalCodeLabel = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 12px 0;
`;

const SyncDisconnectModalCodeGroup = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
  margin-bottom: 20px;
`;

const SyncDisconnectModalCodeBox = styled.div`
  flex: 1;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  border: 1px solid ${props => props.theme.colors.border.ce};
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: ${props => props.theme.colors.white};
  box-sizing: border-box;
`;

const SyncDisconnectModalDesc = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 24px 0;
`;

const SyncDisconnectModalButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const SyncDisconnectModalBtn = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.$primary
    ? `
      background: #1a1a1a;
      color: white;
      border: 1px solid #1a1a1a;
      &:hover { background: #333; }
    `
    : `
      background: white;
      color: #333;
      border: 1px solid #d0d0d0;
      &:hover { background: #f5f5f5; }
    `}
`;

// ============================================================================
// 학생용 동기화 플로팅 버튼 및 모달 스타일
// ============================================================================
const SyncFloatingButton = styled.button`
  position: fixed;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px;
  background: #1a1a1a;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-50%) scale(1.05);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.4);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 12px;
    right: 12px;
    width: 8px;
    height: 8px;
    background: #ef4444;
    border-radius: 50%;
  }
`;

const SyncFloatingIcon = styled.div`
  width: 32px;
  height: 32px;
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 18px;
    height: 18px;
    color: white;
  }
`;

const SyncFloatingText = styled.span`
  color: white;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
`;

const SyncRequestContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.xl};
`;

const SyncRequestTitle = styled.h3`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.normal};
  color: ${props => props.theme.colors.book.color2b};
  line-height: 162%;
  width: 100%;
  text-align: left;
`;

const SyncRequestPageNo = styled.div`
  font-size: ${props => props.theme.fontSize['2xl']};
  font-weight: ${props => props.theme.fontWeight.bold};
  color: ${props => props.theme.colors.book.color2b};
`;

const SyncRequestButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
`;

const QuizAnswer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.lg};
  width: 100%;
`;

const QuizAnswerButton = styled.button<{ $variant: 'yes' | 'no'; $active?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: calc(50% - ${props => props.theme.spacing.md} / 2);
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: #F9F9F9;
  outline: none;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.2s;
  span {
    font-size: ${props => props.theme.fontSize.base};
    font-weight: ${props => props.theme.fontWeight.bold};
    line-height: 100%;
  }
  strong {
    position: absolute;
    top: -13px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 ${props => props.theme.spacing.md};
    height: 26px;
    border-radius: 13px;
    color: white;
    font-size: ${props => props.theme.fontSize.sm};
    font-weight: ${props => props.theme.fontWeight.normal};
  }
  ${props => props.$variant === 'yes' ? `
    ${props.$active ? `
      border-color: #2078DC;
      background-color: #EBF3FF;
    ` : ''}
    &:hover {
      border-color: #2078DC;
    }
    span {
      color: #2078DC;
    }
    strong {
      background-color: #2078DC;
    }
  ` : `
    ${props.$active ? `
      border-color: #D94B4B;
      background-color: #FFF0F0;
    ` : ''}
    &:hover {
      border-color: #D94B4B;
    }
    span {
      color: #D94B4B;
    }
    strong {
      background-color: #D94B4B;
    }
  `}
`;


const SyncRequestButton = styled.button<{ $variant: 'accept' | 'reject' }>`
  flex: 1;
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  height: 44px;
  
  ${props => props.$variant === 'accept' ? `
    background: ${props.theme.colors.book.color1b};
    color: white;
    border: none;
    
    &:disabled {
      cursor: not-allowed;
      opacity: 0.8;
    }
  ` : `
    background: white;
    color: ${props.theme.colors.gray[700]};
    border: 1px solid ${props.theme.colors.gray[300]};
    
    &:hover {
      background: ${props.theme.colors.gray[50]};
    }
  `}
`;

// ============================================================================
// 공유 폴더 수신 토스트 스타일
// ============================================================================
const SharedFolderToast = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%) translateY(${props => props.$isVisible ? '0' : '100px'});
  background: var(--bg-secondary);
  color: var(--text-primary);
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.lg};
  box-shadow: ${props => props.theme.shadows.xl};
  border: 1px solid var(--modal-border);
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  z-index: 9999;
  opacity: ${props => props.$isVisible ? 1 : 0};
  transition: all 0.3s ease;
  min-width: 320px;
  max-width: 90%;
`;

const SharedFolderToastHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const SharedFolderToastIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.primary[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 24px;
    height: 24px;
    color: ${props => props.theme.colors.primary[600]};
  }
`;

const SharedFolderToastContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const SharedFolderToastTitle = styled.span`
  font-size: ${props => props.theme.fontSize.base};
  font-weight: ${props => props.theme.fontWeight.semibold};
  color: var(--text-primary);
`;

const SharedFolderToastMessage = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: var(--text-secondary);
`;

const SharedFolderToastClose = styled.button`
  background: none;
  border: none;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 1;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// ============================================================================
// 폰트 설정 모달 스타일
// ============================================================================
const FontSettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FontSelect = styled.select`
  width: 100%;
  height: 42px;
  padding: 12px 14px;
  margin-bottom: 4px;
  border: 1px solid ${props => props.theme.colors.border.de};
  border-radius: ${props => props.theme.borderRadius.lg};
  font-size: 13px;
  font-family: inherit;
  color: ${props => props.theme.colors.book.color2b};
  cursor: pointer;
  transition: border-color 0.2s;
  -webkit-appearance: none;
  appearance: none;
  background-color: ${props => props.theme.colors.border.fe};
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='8' viewBox='0 0 12 8' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 1.5L6 6.5L11 1.5' stroke='%23666' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  outline: none;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.book.color9b};
  }
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SliderRowLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.book.color4b};
  white-space: nowrap;
  min-width: 30px;
  flex-shrink: 0;
`;

const SliderIndicator = styled.span<{ $fontSize?: number; $fontWeight?: number }>`
  font-size: ${props => props.$fontSize ?? 14}px;
  font-weight: ${props => props.$fontWeight ?? 400};
  color: ${props => props.theme.colors.border.be};
  white-space: nowrap;
  min-width: 24px;
  text-align: center;
  flex-shrink: 0;
  user-select: none;
`;

const SliderColumn = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
  padding: 8px 0;
`;

const SliderTrack = styled.div<{ $min?: number; $max?: number }>`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 6px;
  background: ${props => props.theme.colors.border.de};
  border-radius: 3px;
  transform: translateY(-50%);
`;

const SliderFill = styled.div<{ $percent: number }>`
  position: absolute;
  top: 50%;
  left: 0;
  width: ${props => props.$percent}%;
  height: 6px;
  background: ${props => props.theme.colors.book.color9b};
  border-radius: 3px;
  transform: translateY(-50%);
  pointer-events: none;
`;

const SnapDotsRow = styled.div`
  position: absolute;
  left: 0;
  bottom: 12px;
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 2px 16px 0;
`;

const SnapDot = styled.div`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${props => props.theme.colors.book.color4b};
`;

const SnapPoint = styled.div<{ $position: number; $min: number; $max: number }>`
  position: absolute;
  top: 50%;
  left: ${props => `${((props.$position - props.$min) / (props.$max - props.$min)) * 100}%`};
  width: 2px;
  height: 12px;
  background: ${props => props.theme.colors.gray[400]};
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const FontSettingSlider = styled.input`
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: transparent;
  outline: none;
  -webkit-appearance: none;
  z-index: 1;
  transform: translateY(-50%);

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 30px;
    height: 20px;
    border-radius: 99px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
    cursor: pointer;
    position: relative;
    z-index: 2;
  }

  &::-moz-range-thumb {
    width: 30px;
    height: 20px;
    border-radius: 99px;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
    cursor: pointer;
    position: relative;
    z-index: 2;
  }

  &::-moz-range-track {
    background: transparent;
  }
`;

const FontSettingsButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 4px;
`;

const FontSettingsButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 14px 16px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  ${props => props.$variant === 'primary' ? `
    background: ${props.theme.colors.book.color2b};
    color: ${props.theme.colors.white};
    border: none;

    &:hover {
      background: ${props.theme.colors.book.color3b};
    }
  ` : `
    background: ${props.theme.colors.white};
    color: ${props.theme.colors.book.color6b};
    border: 1px solid ${props.theme.colors.border.de};

    &:hover {
      background: ${props.theme.colors.border.fe};
    }
  `}
`;

const PageViewToggleGroup = styled.div`
  display: flex;
  gap: 4px;
`;

const PageViewToggleBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: ${props => props.$active ? 600 : 400};
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid ${props => props.$active ? '#222' : '#ddd'};
  background: ${props => props.$active ? '#f5f5f5' : 'white'};
  color: ${props => props.$active ? '#222' : '#888'};

  &:hover {
    border-color: #222;
    background: ${props => props.$active ? '#f5f5f5' : '#fafafa'};
  }
`;

const FontSettingLabel = styled.label`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.colors.book.color4b};
  margin-bottom: 4px;
`;

const FontSettingItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const FontSettingValue = styled.span`
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.primary[600]};
  font-weight: ${props => props.theme.fontWeight.bold};
`;

const FontPreview = styled.div<{ $fontFamily: string; $fontSize: number; $fontWeight: number; $letterSpacing: number; $lineHeight: number }>`
  padding: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.gray[50]};
  border-radius: ${props => props.theme.borderRadius.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  font-family: ${props => props.$fontFamily === 'inherit' ? 'inherit' : `"${props.$fontFamily}", sans-serif`};
  font-size: ${props => props.$fontSize}px;
  font-weight: ${props => props.$fontWeight};
  letter-spacing: ${props => `${letterSpacingToEm(props.$letterSpacing)}em`};
  line-height: ${props => props.$lineHeight};
  color: ${props => props.theme.colors.gray[800]};
  min-height: 48px;
  max-height: 88px;
  overflow: auto;
`;

const TabBar = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background: var(--bg-primary, #fff);
  border-bottom: 1px solid var(--border-color, #e0e0e0);
  overflow-x: auto;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--border-color, #ccc);
    border-radius: 2px;
  }
`;

const Tab = styled.button<{ $isActive?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border: 1px solid ${props => props.$isActive ? 'var(--primary-color, #3b82f6)' : 'var(--border-color, #e0e0e0)'};
  border-radius: 20px;
  background: ${props => props.$isActive ? 'var(--primary-bg, #eff6ff)' : 'var(--bg-secondary, #f5f5f5)'};
  color: ${props => props.$isActive ? 'var(--primary-color, #3b82f6)' : 'var(--text-primary, #333)'};
  font-size: 13px;
  font-weight: ${props => props.$isActive ? '600' : '400'};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  
  &:hover {
    border-color: var(--primary-color, #3b82f6);
    background: ${props => props.$isActive ? 'var(--primary-bg, #eff6ff)' : 'var(--bg-tertiary, #ebebeb)'};
  }
`;

const TabCloseButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: transparent;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
  
  svg {
    width: 12px;
    height: 12px;
  }
`;

const AddTabButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px dashed var(--border-color, #e0e0e0);
  border-radius: 50%;
  background: transparent;
  color: var(--text-secondary, #666);
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
  
  &:hover {
    border-color: var(--primary-color, #3b82f6);
    color: var(--primary-color, #3b82f6);
    background: var(--bg-secondary, #f5f5f5);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const SyncRoomInfo = styled.div`
  text-align: center;
  font-size: ${props => props.theme.fontSize.sm};
  color: ${props => props.theme.colors.gray[500]};
  margin-top: ${props => props.theme.spacing.md};
`;

interface PdfTab {
  id: string;
  fileName: string;
  fileUuid: string;
}

interface LayoutProps {
  children: React.ReactNode;
  hideAnnotations?: boolean;
  onToggleAnnotations?: () => void;
  onSync?: () => void;
  onFontSettings?: () => void;
  onVoiceRecord?: () => void;
  isRecording?: boolean;
  isViewSyncActive?: boolean;
  isSyncActive?: boolean;
  bookUuid?: string; // 동기화 소켓 생성에 필요한 책 UUID
  currentPageNo?: number; // 현재 페이지 번호 (교수자 View 전송용, URL 기반)
  getCurrentVisiblePageNo?: () => number; // 실제 화면에 표시 중인 페이지 번호 반환
  onNavigateToPage?: (pageNo: number) => void; // 페이지 이동 함수 (학생 동기화용)
  bookUid?: string; // 첨부파일용 책 UID (bookUuid와 동일)
  onBackToList?: () => void; // 목록으로 돌아가기
  showSyncLocationButton?: boolean; // 동기화 위치 버튼 표시 여부
  syncPendingPageNo?: number | null; // 동기화 대기 중인 페이지 번호
  onSyncLocationClick?: () => void; // 동기화 위치 버튼 클릭 핸들러
  onQuizCreateClick?: (params: { selectedText: string; showTextTooLongModal?: boolean }) => void; // 퀴즈 생성 버튼 클릭
  convertPageNoDbToIdx?: (pageNoDb: number) => number | null; // page_no(DB) → idx(표시용) 변환
  onOpenLearningRecords?: () => void;
  onOpenAIPanel?: () => void;
  isAIPanelOpen?: boolean;
  isLearningRecordsOpen?: boolean;
}

/**
 * 4자리 난수 생성
 */
const generateSyncCode = (): string => {
  return Math.floor(1000 + Math.random() * 9000).toString();
};

const Layout: React.FC<LayoutProps> = ({ 
  children, 
  hideAnnotations = false, 
  onToggleAnnotations,
  onSync,
  onFontSettings,
  onVoiceRecord,
  isRecording = false,
  isViewSyncActive = false,
  isSyncActive = false,
  bookUuid,
  currentPageNo,
  onNavigateToPage,
  bookUid,
  onBackToList,
  showSyncLocationButton = false,
  syncPendingPageNo = null,
  onSyncLocationClick,
  onQuizCreateClick,
  convertPageNoDbToIdx,
  getCurrentVisiblePageNo,
  onOpenLearningRecords,
  onOpenAIPanel,
  isAIPanelOpen = false,
  isLearningRecordsOpen = false,
}) => {
  const { user, logout, isAuthenticated, token } = useFrontAuthStore();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const location = useLocation();
  const isProfessor = user?.role === 'professor';
  const isStudent = user?.role === 'student';

  const devicePlatform = React.useMemo(() => {
    const ua = navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
      return 'ios';
    }
    if (/Android/i.test(ua)) return 'android';
    return 'desktop';
  }, []);
  
  // 실제 사용할 bookUid (bookUuid 또는 bookUid)
  const effectiveBookUid = bookUid || bookUuid || '';
  
  // /api-book/viewer/ 경로에서만 IconGroup 표시
  const isBookViewerPage = location.pathname.startsWith('/api-book/viewer/');

  // 도서 제목 (뷰어 페이지에서 표시)
  const [bookTitle, setBookTitle] = useState<string | null>(null);

  // 동기화 모달 상태
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [syncCode, setSyncCode] = useState<string>('');
  const [studentInputCode, setStudentInputCode] = useState<string[]>(['', '', '', '']);
  const [syncRoomData, setSyncRoomData] = useState<SyncRoomData | SyncResolveData | null>(null);
  const [isSyncLoading, setIsSyncLoading] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  // 동기화 해제 확인 모달 상태
  const [isSyncDisconnectModalOpen, setIsSyncDisconnectModalOpen] = useState(false);

  // 학생용 동기화 요청 모달/플로팅 버튼 상태
  const [isSyncRequestModalOpen, setIsSyncRequestModalOpen] = useState(false);
  
  // 학생용 퀴즈 수신 모달 상태 (큐 방식)
  const [isQuizReceivedModalOpen, setIsQuizReceivedModalOpen] = useState(false);
  const [quizQueue, setQuizQueue] = useState<Array<{
    question: string;
    answer?: string; // 레거시 호환
    explanation?: string; // 레거시 호환
    correct_answer?: string; // API 정답 (O/X)
    hint?: string; // API 해설
    id: string; // 고유 ID
    delivery_uuid?: string; // Delivery UUID (API 호출용)
    viewer_uuid?: string; // Viewer UUID (API 호출용)
  }>>([]);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
  const [currentQuizAnswer, setCurrentQuizAnswer] = useState<'O' | 'X' | null>(null);
  const [showCurrentQuizResult, setShowCurrentQuizResult] = useState(false);
  // 이미 opened API를 호출한 delivery_uuid 추적 (중복 호출 방지)
  const openedDeliveryUuidsRef = useRef<Set<string>>(new Set());
  
  const BOOK_TAB_ID = 'book';
  const [activeTabId, setActiveTabId] = useState<string>(BOOK_TAB_ID);
  const [pdfTabs, setPdfTabs] = useState<PdfTab[]>([]);
  
  // 교수의 노트 모달 상태 (학생 전용)
  const [isProfessorNotesModalOpen, setIsProfessorNotesModalOpen] = useState(false);
  const [isAttachmentModalOpen, setIsAttachmentModalOpen] = useState(false);

  // 아이콘 pop 애니메이션: 패널이 닫힐 때 트리거
  const [isAIPanelPop, setIsAIPanelPop] = useState(false);
  const [isLearningRecordsPop, setIsLearningRecordsPop] = useState(false);
  const prevAIPanelOpenRef = useRef(isAIPanelOpen);
  const prevLearningRecordsOpenRef = useRef(isLearningRecordsOpen);
  useEffect(() => {
    if (prevAIPanelOpenRef.current && !isAIPanelOpen) {
      setIsAIPanelPop(true);
    }
    prevAIPanelOpenRef.current = isAIPanelOpen;
  }, [isAIPanelOpen]);
  useEffect(() => {
    if (prevLearningRecordsOpenRef.current && !isLearningRecordsOpen) {
      setIsLearningRecordsPop(true);
    }
    prevLearningRecordsOpenRef.current = isLearningRecordsOpen;
  }, [isLearningRecordsOpen]);
  const [pendingPageNo, setPendingPageNo] = useState<number | null>(null);
  const [showSyncFloatingButton, setShowSyncFloatingButton] = useState(false);
  
  // 공유 폴더 수신 상태
  const [receivedSharedFolder, setReceivedSharedFolder] = useState<SharedFolderPublishData | null>(null);
  const [showSharedFolderToast, setShowSharedFolderToast] = useState(false);
  
  // 햄버거 메뉴 드롭다운 상태
  const [isMenuDropdownOpen, setIsMenuDropdownOpen] = useState(false);
  const menuDropdownRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();

  // WebSocket 연결 (동기화 소켓)
  const wsUrl = syncRoomData ? ('wsUrl' in syncRoomData ? syncRoomData.wsUrl : null) : null;
  
  // 학생 역할 확인 로그
  useEffect(() => {
    if (isStudent) {
      // console.log('[Layout] 학생 WebSocket 설정:', {
      //   wsUrl,
      //   syncCode,
      //   isProfessor,
      //   isStudent,
      //   hasSyncRoomData: !!syncRoomData,
      // });
    }
  }, [wsUrl, syncCode, isProfessor, isStudent, syncRoomData]);

  // 뷰어 페이지에서 도서 제목 조회
  useEffect(() => {
    if (!isBookViewerPage || !effectiveBookUid) {
      setBookTitle(null);
      return;
    }
    const fetchBookTitle = async () => {
      try {
        const response = await apiClient.get<{
          success: boolean;
          data?: { items?: Array<{ book_uuid: string; title: string }> };
        }>('/viewer/books/my/books?limit=100&offset=0');
        if (response.data.success && response.data.data?.items) {
          const found = response.data.data.items.find(
            (item) => item.book_uuid === effectiveBookUid
          );
          setBookTitle(found?.title ?? null);
        } else {
          setBookTitle(null);
        }
      } catch {
        setBookTitle(null);
      }
    };
    fetchBookTitle();
  }, [isBookViewerPage, effectiveBookUid]);
  
  const syncSocketResult = useSyncSocket({
    wsUrl,
    joinCode: syncCode || undefined,
    isProfessor: isProfessor || false,
    token: token,
    roomUuid: syncRoomData ? ('roomUuid' in syncRoomData ? syncRoomData.roomUuid : null) : null,
    onSyncRequest: (request: SyncRequest) => {
      // 학생: 동기화 요청 수신 시 모달 표시
      // 교수자가 전송하는 page_no는 1-based이므로 0-based idx로 변환 (-1)
      const adjustedPageNo = request.pageNo > 0 ? request.pageNo - 1 : request.pageNo;
      setPendingPageNo(adjustedPageNo);
      setIsSyncRequestModalOpen(true);
    },
    onQuizPublish: (data) => {
      // 학생: 퀴즈 발송 수신 시 큐에 추가
      // console.log('[Layout] 퀴즈 발송 수신 콜백 호출:', {
      //   data,
      //   hasQuizzes: !!(data && data.quizzes),
      //   quizzesLength: data?.quizzes?.length,
      //   isStudent,
      //   isProfessor,
      // });
      if (data && data.quizzes && data.quizzes.length > 0) {
        // 받은 퀴즈들을 큐에 추가 (고유 ID 부여 및 delivery_uuid, viewer_uuid 저장)
        // API는 correct_answer, hint 사용 / 레거시는 answer, explanation 사용
        const newQuizzes = data.quizzes.map((quiz: any, index: number) => ({
          ...quiz,
          id: `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
          delivery_uuid: quiz.delivery_uuid || quiz.deliveryUuid,
          viewer_uuid: bookUuid || bookUid, // bookUuid 또는 bookUid를 viewer_uuid로 사용
          correct_answer: quiz.correct_answer ?? quiz.answer,
          hint: quiz.hint ?? quiz.explanation ?? '',
        }));
        
        // console.log('[Layout] 퀴즈 큐에 추가:', { 
        //   newQuizzesCount: newQuizzes.length,
        //   newQuizzes: newQuizzes,
        // });
        
        setQuizQueue(prev => {
          const updatedQueue = [...prev, ...newQuizzes];
          return updatedQueue;
        });
        
        // 모달이 닫혀있으면 열기 (함수형 업데이트로 최신 상태 확인)
        setIsQuizReceivedModalOpen(prevModalState => {
          if (!prevModalState) {
            // 모달이 닫혀있으면 열기
            setCurrentQuizIndex(0);
            setCurrentQuizAnswer(null);
            setShowCurrentQuizResult(false);
            return true;
          }
          // 모달이 이미 열려있으면 상태 유지
          return prevModalState;
        });
      } else {
        // console.warn('[Layout] 퀴즈 데이터가 올바르지 않습니다:', data);
      }
    },
    onSharedFolderPublish: (data) => {
      // 학생: 공유 폴더 발송 수신 시 토스트 알림 표시
      // console.log('[Layout] 공유 폴더 발송 수신 콜백 호출:', data);
      if (data && data.folder_uuid) {
        setReceivedSharedFolder(data);
        setShowSharedFolderToast(true);
      }
    },
  });

  const {
    isConnected: isSyncSocketConnected,
    connect: connectSyncSocket,
    disconnect: disconnectSyncSocket,
    sendSyncStart,
    sendQuizPublish,
    sendSharedFolderPublish,
    pendingSyncRequest,
    clearPendingRequest,
    wasAbnormalDisconnect,
  } = syncSocketResult;

  // 퀴즈 큐가 변경될 때 currentQuizIndex 유효성 검증
  useEffect(() => {
    if (quizQueue.length > 0) {
      // currentQuizIndex가 마지막 문제를 넘어가면 모달 닫기 (모든 문제 완료)
      if (currentQuizIndex >= quizQueue.length) {
        setIsQuizReceivedModalOpen(false);
        setQuizQueue([]);
        setCurrentQuizIndex(0);
        setCurrentQuizAnswer(null);
        setShowCurrentQuizResult(false);
      }
    } else {
      // 큐가 비어있으면 모달 닫기
      if (isQuizReceivedModalOpen) {
        setIsQuizReceivedModalOpen(false);
        setCurrentQuizIndex(0);
        setCurrentQuizAnswer(null);
        setShowCurrentQuizResult(false);
      }
    }
  }, [quizQueue.length, currentQuizIndex, isQuizReceivedModalOpen]);

  // 퀴즈 모달이 열리고 현재 퀴즈가 있을 때 opened API 호출 (중복 호출 방지)
  useEffect(() => {
    if (isQuizReceivedModalOpen && quizQueue.length > 0 && currentQuizIndex < quizQueue.length) {
      const currentQuiz = quizQueue[currentQuizIndex];
      const deliveryUuid = currentQuiz.delivery_uuid;
      const viewerUuid = currentQuiz.viewer_uuid;
      
      if (deliveryUuid && viewerUuid) {
        // 이미 opened API를 호출한 delivery_uuid인지 확인
        if (openedDeliveryUuidsRef.current.has(deliveryUuid)) {
          // console.log('[Layout] 퀴즈 opened API 이미 호출됨, 스킵:', {
          //   delivery_uuid: deliveryUuid,
          // });
          return;
        }
        
        // console.log('[Layout] 퀴즈 opened API 호출:', {
        //   delivery_uuid: deliveryUuid,
        //   viewer_uuid: viewerUuid,
        // });
        
        // 호출한 delivery_uuid를 Set에 추가 (중복 호출 방지)
        openedDeliveryUuidsRef.current.add(deliveryUuid);
        
        quizImageService.markQuizOpened(viewerUuid, deliveryUuid)
          .then((result) => {
            // console.log('[Layout] 퀴즈 opened API 성공:', result);
          })
          .catch(() => {
            // opened API 오류 시 예외처리하고 그냥 넘어감 (재시도하지 않음)
          });
      } else {
        // console.warn('[Layout] 퀴즈 opened API 호출 불가: delivery_uuid 또는 viewer_uuid가 없음', {
        //   delivery_uuid: deliveryUuid,
        //   viewer_uuid: viewerUuid,
        // });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isQuizReceivedModalOpen, currentQuizIndex, quizQueue.length]);

  // 동기화 소켓 연결 (syncRoomData 변경 시)
  // 참고: connectSyncSocket, disconnectSyncSocket을 의존성에서 제외하여 무한 루프 방지
  useEffect(() => {
    // console.log('[Layout] 동기화 소켓 연결 useEffect 실행:', {
    //   hasSyncRoomData: !!syncRoomData,
    //   wsUrl,
    //   isProfessor,
    //   syncCode: syncCode || '없음',
    //   role: isProfessor ? '교수자' : '학생',
    // });

    if (syncRoomData && wsUrl) {
      // 학생인 경우 syncCode가 설정될 때까지 대기
      if (!isProfessor && !syncCode) {
        // console.log('[Layout] 학생: syncCode 대기 중...', {
        //   syncCode,
        //   hasSyncRoomData: !!syncRoomData,
        //   wsUrl,
        // });
        return;
      }
      
      // 약간의 지연 후 연결 시도 (상태 업데이트 완료 대기)
      const timer = setTimeout(() => {
        // console.log('[Layout] 소켓 연결 시작 (setTimeout 실행)', { 
        //   isProfessor, 
        //   syncCode: syncCode || '없음',
        //   role: isProfessor ? '교수자' : '학생',
        //   wsUrl,
        //   hasSyncRoomData: !!syncRoomData,
        // });
        connectSyncSocket();
      }, 200);
      
      // console.log('[Layout] setTimeout 설정 완료, 200ms 후 연결 시도');
      
      return () => {
        // console.log('[Layout] useEffect cleanup 실행 - 타이머 정리 및 소켓 연결 해제');
        clearTimeout(timer);
        // 정상 종료
        disconnectSyncSocket();
      };
    } else {
      // console.log('[Layout] 소켓 연결 조건 불만족:', {
      //   hasSyncRoomData: !!syncRoomData,
      //   hasWsUrl: !!wsUrl,
      // });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncRoomData, wsUrl, syncCode, isProfessor]);

  // 비정상 종료 감지 및 로깅
  useEffect(() => {
    if (wasAbnormalDisconnect) {
      const role = isProfessor ? '교수자' : '학생';
      // console.warn(`[동기화] ${role} 소켓 비정상 종료 감지:`, {
      //   role,
      //   wasAbnormalDisconnect,
      //   syncCode: syncCode || '없음',
      //   hasSyncRoomData: !!syncRoomData,
      // });
    }
  }, [wasAbnormalDisconnect, isProfessor, syncCode, syncRoomData]);

  // 학생: 책 뷰어 페이지 진입 시 미풀이(pending) 퀴즈 조회 (소켓 연결과 무관)
  useEffect(() => {
    if (!isStudent || !isBookViewerPage) return;
    const viewerUuid = bookUuid || bookUid;
    if (!viewerUuid) return;

    quizImageService.fetchPendingQuizzes(viewerUuid)
      .then((pendingQuizzes) => {
        if (pendingQuizzes.length === 0) return;
        // console.log('[Layout] 미풀이 퀴즈 로드:', { count: pendingQuizzes.length });

        const newQuizzes = pendingQuizzes.map((quiz, index) => ({
          ...quiz,
          id: `pending-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
          delivery_uuid: quiz.delivery_uuid,
          viewer_uuid: viewerUuid,
          correct_answer: quiz.correct_answer,
          hint: quiz.hint ?? '',
        }));

        setQuizQueue(prev => {
          const existingDeliveryUuids = new Set(prev.map(q => q.delivery_uuid));
          const filtered = newQuizzes.filter(q => !existingDeliveryUuids.has(q.delivery_uuid));
          if (filtered.length === 0) return prev;
          // console.log('[Layout] 미풀이 퀴즈 큐 추가:', { added: filtered.length, skipped: newQuizzes.length - filtered.length });
          return [...prev, ...filtered];
        });

        setIsQuizReceivedModalOpen(prevModalState => {
          if (!prevModalState) {
            setCurrentQuizIndex(0);
            setCurrentQuizAnswer(null);
            setShowCurrentQuizResult(false);
            return true;
          }
          return prevModalState;
        });
      })
      .catch((error) => {
        // console.error('[Layout] 미풀이 퀴즈 조회 실패:', error);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBookViewerPage, isStudent, bookUuid, bookUid]);

  // 학생: 동기화 요청 수락
  const handleAcceptSync = useCallback(() => {
    if (pendingPageNo !== null && onNavigateToPage) {
      onNavigateToPage(pendingPageNo);
    }
    setIsSyncRequestModalOpen(false);
    setPendingPageNo(null);
    setShowSyncFloatingButton(false);
    
    // Custom Event로 BookViewer에 알림 (버튼 숨기기)
    window.dispatchEvent(new CustomEvent('syncLocationButtonShow', {
      detail: {
        showSyncLocationButton: false,
        syncPendingPageNo: null,
      }
    }));
    
    clearPendingRequest();
  }, [pendingPageNo, onNavigateToPage, clearPendingRequest]);


  // 학생: 플로팅 버튼 클릭 (나중에 이동) - BookViewer로 이동
  const handleFloatingButtonClick = useCallback(() => {
    setIsSyncRequestModalOpen(true);
  }, []);

  // 교수자: View 버튼 클릭 (현재 페이지 동기화 전송)
  const handleViewClick = useCallback(() => {
    const pageNo = getCurrentVisiblePageNo ? getCurrentVisiblePageNo() : currentPageNo;
    if (pageNo !== undefined && isSyncSocketConnected) {
      const success = sendSyncStart(pageNo);
      if (!success) {
        // React 상태(isSyncSocketConnected)는 true지만 실제 WebSocket 연결이 끊어진 경우
        console.warn('[동기화] View 전송 실패: WebSocket 연결 상태 불일치');
        alert('동기화 연결이 끊어졌습니다. 동기화를 다시 시작해주세요.');
      }
    } else if (!isSyncSocketConnected) {
      alert('동기화 소켓이 연결되어 있지 않습니다. 먼저 동기화를 시작해주세요.');
    }
  }, [getCurrentVisiblePageNo, currentPageNo, isSyncSocketConnected, sendSyncStart]);

  // 폰트 설정 모달 상태 및 Context 사용
  const [isFontModalOpen, setIsFontModalOpen] = useState(false);
  const { fontSettings, updateFontSettings, reloadFontSettings } = useFontSettings();

  // 모달 열 때 스냅샷 (취소 시 복원용)
  const fontSettingsSnapshotRef = useRef<FontSettings | null>(null);
  const initialPagesPerViewRef = useRef<1 | 2>(1);

  // 모달 내부 draft (적용 전까지 Context/localStorage에 반영하지 않음)
  const [draftFontSettings, setDraftFontSettings] = useState<FontSettings>(() => fontSettings);

  // 페이지 보기 모드 (1쪽/2쪽)
  const [pagesPerView, setPagesPerView] = useState<1 | 2>(() => getSettings().pagesPerView);

  const handlePagesPerViewChange = useCallback((value: 1 | 2) => {
    setPagesPerView(value);
    updateBookViewerSettings({ pagesPerView: value });
    window.dispatchEvent(new CustomEvent('pagesPerViewChange', { detail: value }));
  }, []);

  // 폰트 설정 변경 시 CSS 변수 적용 (모달 열려 있으면 draft 미리보기)
  useEffect(() => {
    applyFontSettings(isFontModalOpen ? draftFontSettings : fontSettings);
  }, [fontSettings, draftFontSettings, isFontModalOpen]);

  // draft 변경 핸들러 (Context에 반영하지 않음)
  const handleDraftFontSettingChange = useCallback((key: keyof FontSettings, value: number | string) => {
    setDraftFontSettings(prev => ({ ...prev, [key]: value }));
  }, []);

  // 드래그 중 로컬 상태 (리렌더링 방지)
  const [localSliderValues, setLocalSliderValues] = useState<{
    fontSize?: number;
    fontWeight?: number;
    letterSpacing?: number;
    lineHeight?: number;
  }>({});
  const isDraggingRef = useRef<boolean>(false);

  // 슬라이더 핸들러 (드래그 중 로컬 상태만 업데이트)
  const handleSliderChange = useCallback((
    key: keyof FontSettings,
    value: number,
    _snapPoints: number[]
  ) => {
    if (key === 'fontSize') {
      setLocalSliderValues(prev => ({ ...prev, fontSize: Math.round(value) }));
    } else if (key === 'fontWeight') {
      setLocalSliderValues(prev => ({ ...prev, fontWeight: value }));
    } else if (key === 'letterSpacing') {
      setLocalSliderValues(prev => ({ ...prev, letterSpacing: value }));
    } else if (key === 'lineHeight') {
      setLocalSliderValues(prev => ({ ...prev, lineHeight: value }));
    }
  }, []);

  // 슬라이더 마우스 다운 핸들러
  const handleSliderMouseDown = useCallback(() => {
    isDraggingRef.current = true;
  }, []);

  // 슬라이더 마우스 업 핸들러 (draft에만 반영)
  const createSliderMouseUpHandler = useCallback((
    key: keyof FontSettings,
    _snapPoints: number[]
  ) => {
    return (e: React.MouseEvent<HTMLInputElement> | React.TouchEvent<HTMLInputElement>) => {
      isDraggingRef.current = false;
      const target = e.currentTarget;
      const currentValue = Number(target.value);

      if (key === 'fontSize') {
        const finalValue = Math.round(currentValue);
        handleDraftFontSettingChange(key, finalValue);
        setLocalSliderValues(prev => ({ ...prev, fontSize: undefined }));
      } else if (key === 'fontWeight') {
        const { min: fwMin, max: fwMax } = FONT_MODAL_RANGES.fontWeight;
        const clampedValue = Math.max(fwMin, Math.min(fwMax, currentValue));
        const fontWeightValue = valueToFontWeight(clampedValue);
        handleDraftFontSettingChange(key, fontWeightValue);
        setLocalSliderValues(prev => ({ ...prev, fontWeight: undefined }));
      } else if (key === 'letterSpacing') {
        const { min, max } = FONT_MODAL_RANGES.letterSpacing;
        const clampedValue = Math.max(min, Math.min(max, currentValue));
        handleDraftFontSettingChange(key, clampedValue);
        setLocalSliderValues(prev => ({ ...prev, letterSpacing: undefined }));
      } else if (key === 'lineHeight') {
        const { min: lhMin, max: lhMax } = FONT_MODAL_RANGES.lineHeight;
        const clampedValue = Math.max(lhMin, Math.min(lhMax, currentValue));
        handleDraftFontSettingChange(key, valueToLineHeight(clampedValue));
        setLocalSliderValues(prev => ({ ...prev, lineHeight: undefined }));
      }
    };
  }, [handleDraftFontSettingChange]);

  // 폰트 설정 적용 (저장)
  const handleSaveFontSettings = useCallback(() => {
    updateFontSettings(draftFontSettings);
    reloadFontSettings();
    setIsFontModalOpen(false);
    window.location.reload();
  }, [draftFontSettings, updateFontSettings, reloadFontSettings]);

  // 폰트 설정 모달 내 draft 초기화 (기본값으로 되돌림)
  const handleDraftReset = useCallback(() => {
    setDraftFontSettings({ ...DEFAULT_FONT_SETTINGS });
    setLocalSliderValues({});
    setPagesPerView(1);
    updateBookViewerSettings({ pagesPerView: 1 });
    window.dispatchEvent(new CustomEvent('pagesPerViewChange', { detail: 1 }));
  }, []);

  const handleFontSettingChange = useCallback((key: keyof FontSettings, value: number | string) => {
    const newSettings = { ...fontSettings, [key]: value };
    updateFontSettings(newSettings);
  }, [fontSettings, updateFontSettings]);

  const handleResetFontSettings = useCallback(() => {
    updateFontSettings(DEFAULT_FONT_SETTINGS);
  }, [updateFontSettings]);

  // 폰트 설정 모달 열기
  const handleOpenFontModal = useCallback(() => {
    const current = getSettings().pagesPerView;
    initialPagesPerViewRef.current = current;
    setPagesPerView(current);
    fontSettingsSnapshotRef.current = { ...fontSettings };
    setDraftFontSettings({ ...fontSettings });
    setLocalSliderValues({});
    setIsFontModalOpen(true);
    onFontSettings?.();
  }, [onFontSettings, fontSettings]);

  // 폰트 설정 모달 닫기 (취소 - 스냅샷으로 복원)
  const handleCloseFontModal = useCallback(() => {
    const snapshot = fontSettingsSnapshotRef.current;
    if (snapshot) {
      updateFontSettings(snapshot);
    }
    updateBookViewerSettings({ pagesPerView: initialPagesPerViewRef.current });
    setPagesPerView(initialPagesPerViewRef.current);
    window.dispatchEvent(new CustomEvent('pagesPerViewChange', { detail: initialPagesPerViewRef.current }));
    setLocalSliderValues({});
    setIsFontModalOpen(false);
  }, [updateFontSettings]);

  // 드롭다운 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target as Node)) {
        setIsMenuDropdownOpen(false);
      }
    };

    if (isMenuDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuDropdownOpen]);

  // 로고 클릭 핸들러
  const handleLogoClick = useCallback(() => {
    if (isAuthenticated) {
      navigate('/book/list');
    } else {
      navigate('/');
    }
  }, [navigate, isAuthenticated]);

  // 로그아웃 핸들러
  const handleLogout = useCallback(async () => {
    try {
      await logout();
    } catch (error) {
      // console.error('로그아웃 실패:', error);
    }
  }, [logout]);

  // 햄버거 메뉴 토글 핸들러
  const handleMenuToggle = useCallback(() => {
    setIsMenuDropdownOpen(prev => !prev);
  }, []);

  // 드롭다운 메뉴 아이템 클릭 핸들러
  const handleMenuClick = useCallback((action: 'profile' | 'inquiry' | 'logout' | 'backToList') => {
    setIsMenuDropdownOpen(false);
    
    switch (action) {
      case 'profile':
        // 내 정보 페이지로 이동 (또는 모달 열기)
        navigate('/profile');
        break;
      case 'inquiry':
        // 문의하기 페이지로 이동
        navigate('/inquiry');
        break;
      case 'logout':
        handleLogout();
        break;
      case 'backToList':
        // 목록으로 돌아가기
        if (onBackToList) {
          onBackToList();
        } else {
          navigate('/book/list');
        }
        break;
    }
  }, [navigate, handleLogout, onBackToList]);

  // 첨부파일 모달 열기
  const handleOpenAttachmentModal = useCallback(() => {
    setIsAttachmentModalOpen(true);
  }, []);

  // 첨부파일 모달 닫기
  const handleCloseAttachmentModal = useCallback(() => {
    setIsAttachmentModalOpen(false);
  }, []);

  const attachmentContext = useAttachmentContext();

  const handleOpenPdf = useCallback((attachment: Attachment) => {
    const existingTab = pdfTabs.find(tab => tab.fileUuid === attachment.file_uuid);
    if (existingTab) {
      setActiveTabId(existingTab.id);
      return;
    }
    const newTab: PdfTab = {
      id: `pdf-${attachment.file_uuid}`,
      fileName: attachment.file_name,
      fileUuid: attachment.file_uuid,
    };
    setPdfTabs(prev => [...prev, newTab]);
    setActiveTabId(newTab.id);
  }, [pdfTabs]);

  const handleCloseTab = useCallback((tabId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (tabId === BOOK_TAB_ID) return;
    setPdfTabs(prev => prev.filter(tab => tab.id !== tabId));
    if (activeTabId === tabId) {
      setActiveTabId(BOOK_TAB_ID);
    }
  }, [activeTabId]);

  const handleSelectTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  const activePdfTab = pdfTabs.find(tab => tab.id === activeTabId);

  // 첨부파일 모달에서 PDF 선택 시 - Context로 전달
  const handleOpenPdfFromModal = useCallback(
    (attachment: Attachment) => {
      setIsAttachmentModalOpen(false);
      attachmentContext?.openPdfFromModal(attachment);
    },
    [attachmentContext]
  );

  // 퀴즈 생성 버튼 클릭 시 selection을 mousedown에서 캡처 (클릭 시 포커스 이동으로 selection 사라짐 방지)
  const quizSelectionRef = useRef<string>('');
  const handleQuizCreateMouseDown = useCallback(() => {
    quizSelectionRef.current = window.getSelection()?.toString().trim() || '';
  }, []);
  const handleQuizCreateClick = useCallback(() => {
    const selectedText = quizSelectionRef.current;
    quizSelectionRef.current = '';
    if (selectedText.length > 5000) {
      onQuizCreateClick?.({ selectedText: '', showTextTooLongModal: true });
    } else {
      window.getSelection()?.removeAllRanges();
      onQuizCreateClick?.({ selectedText });
    }
  }, [onQuizCreateClick]);

  // 동기화 버튼 클릭 핸들러
  const handleSyncClick = useCallback(async () => {
    if (isProfessor) {
      // 교수자: API 호출하여 동기화 소켓 생성
      if (!bookUuid) {
        setSyncError('책 정보가 없습니다.');
        setIsSyncModalOpen(true);
        return;
      }
      
      setIsSyncLoading(true);
      setSyncError(null);
      setIsSyncModalOpen(true);
      
      try {
        // 이전에 생성된 소켓이 있는지 확인
        const openRooms = await syncService.getMyOpenRooms();
        
        // 현재 시간에서 2시간 전 시간 계산
        const now = new Date();
        const twoHoursAgo = new Date(now.getTime() - 2 * 60 * 60 * 1000);
        
        // 오늘 날짜에 생성되고 2시간 이내인 리스트만 필터링
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        const recentTodayRooms = openRooms.filter(room => {
          const createdAt = new Date(room.created_at);
          const createdAtDate = new Date(createdAt);
          createdAtDate.setHours(0, 0, 0, 0);
          
          // 오늘 날짜인지 확인
          const isToday = createdAtDate.getTime() === today.getTime();
          // 2시간 이내인지 확인
          const isWithinTwoHours = createdAt.getTime() >= twoHoursAgo.getTime();
          
          return isToday && isWithinTwoHours;
        });
        
        // console.log('[동기화] 오늘 생성되고 2시간 이내인 열린 방 목록:', {
        //   currentTime: now.toISOString(),
        //   twoHoursAgo: twoHoursAgo.toISOString(),
        //   total: openRooms.length,
        //   recentToday: recentTodayRooms.length,
        //   rooms: recentTodayRooms,
        // });
        
        // 쿠키에서 저장된 room_id와 join_code 조회
        const savedRoomId = getSyncRoomIdCookie();
        const savedJoinCode = getSyncJoinCodeCookie();
        
        // console.log('[동기화] 쿠키에서 조회한 값:', {
        //   savedRoomId,
        //   savedJoinCode,
        //   currentBookUuid: bookUuid,
        // });
        
        // 2시간 이내 소켓이 있으면 재조인, 없으면 신규 생성
        if (recentTodayRooms.length > 0) {
          // 현재 book_uuid와 일치하는 방 찾기
          const matchingRoom = recentTodayRooms.find(room => room.book_uuid === bookUuid);
          
          if (matchingRoom) {
            // 현재 book_uuid와 일치하는 방이 있음
            // console.log('[동기화] 현재 book_uuid와 일치하는 방 발견:', matchingRoom);
            
            // 쿠키의 room_id와 조회한 방의 room_uuid 비교
            let joinCodeToUse = '';
            let shouldCloseAndRecreate = false;
            
            if (savedRoomId === matchingRoom.room_uuid && savedJoinCode) {
              // 쿠키의 room_id와 일치하고 join_code가 있으면 쿠키의 join_code 사용
              joinCodeToUse = savedJoinCode;
              // console.log('[동기화] 쿠키의 join_code 사용:', joinCodeToUse);
            } else {
              // 쿠키 값이 꼬였거나 일치하지 않음
              // console.warn('[동기화] 쿠키 값이 일치하지 않거나 없음:', {
              //   savedRoomId,
              //   roomUuid: matchingRoom.room_uuid,
              //   savedJoinCode,
              // });
              
              // 쿠키에 저장된 room_id가 있지만 현재 방과 다르면 이전 소켓 닫기
              if (savedRoomId && savedRoomId !== matchingRoom.room_uuid) {
                try {
                  // console.log('[동기화] 이전 소켓 닫기 시도:', savedRoomId);
                  await syncService.closeSyncRoom(savedRoomId);
                  // console.log('[동기화] 이전 소켓 닫기 완료');
                } catch (error) {
                  // console.error('[동기화] 이전 소켓 닫기 실패:', error);
                  // 닫기 실패해도 계속 진행
                }
              }
              
              // 쿠키 값이 꼬였으므로 현재 방도 닫고 신규 생성
              shouldCloseAndRecreate = true;
            }
            
            if (shouldCloseAndRecreate) {
              // 현재 방 닫고 신규 생성
              try {
                // console.log('[동기화] 쿠키 값 오류로 인한 소켓 닫기 및 신규 생성');
                await syncService.closeSyncRoom(matchingRoom.room_uuid);
                // console.log('[동기화] 소켓 닫기 완료, 신규 생성 진행');
              } catch (error) {
                // console.error('[동기화] 소켓 닫기 실패:', error);
                // 닫기 실패해도 신규 생성 진행
              }
              
              // 쿠키 삭제
              deleteSyncRoomCookies();
              
              // 신규 소켓 생성
              const roomData = await syncService.createSyncRoom({
                bookUuid: bookUuid,
                title: '동기화 수업',
              });
              
              // 쿠키에 room_id와 join_code 저장
              setSyncRoomCookies(roomData.roomUuid, roomData.joinCode, 2); // 2시간 유효
              // console.log('[동기화] 쿠키에 저장 완료:', {
              //   roomId: roomData.roomUuid,
              //   joinCode: roomData.joinCode,
              // });
              
              setSyncCode(roomData.joinCode);
              setSyncRoomData(roomData);
              // console.log('[동기화] 소켓 생성 완료:', roomData);
            } else {
              // 기존 방 정보를 SyncRoomData 형태로 변환
              const existingRoomData: SyncRoomData = {
                roomUuid: matchingRoom.room_uuid,
                wsUrl: matchingRoom.ws_url,
                bookNo: 0, // OpenRoomItem에 없으므로 0으로 설정
                title: '동기화 수업', // OpenRoomItem에 없으므로 기본값
                joinCode: joinCodeToUse,
                expiresAt: '', // OpenRoomItem에 없으므로 빈 문자열
              };
              
              setSyncRoomData(existingRoomData);
              setSyncCode(joinCodeToUse);
              // console.log('[동기화] 기존 방 재조인 설정 완료:', existingRoomData);
            }
          } else {
            // 현재 book_uuid와 일치하는 방이 없음 - 다른 책의 소켓이므로 신규 생성
            // console.log('[동기화] 현재 book_uuid와 일치하는 방 없음, 신규 생성');
            
            // 쿠키에 저장된 room_id가 있으면 이전 소켓 닫기
            if (savedRoomId) {
              try {
                // console.log('[동기화] 다른 책의 소켓이므로 이전 소켓 닫기:', savedRoomId);
                await syncService.closeSyncRoom(savedRoomId);
                // console.log('[동기화] 이전 소켓 닫기 완료');
              } catch (error) {
                // console.error('[동기화] 이전 소켓 닫기 실패:', error);
                // 닫기 실패해도 계속 진행
              }
            }
            
            // 쿠키 삭제
            deleteSyncRoomCookies();
            
            // 신규 소켓 생성
            const roomData = await syncService.createSyncRoom({
              bookUuid: bookUuid,
              title: '동기화 수업',
            });
            
            // 쿠키에 room_id와 join_code 저장
            setSyncRoomCookies(roomData.roomUuid, roomData.joinCode, 2); // 2시간 유효
            // console.log('[동기화] 쿠키에 저장 완료:', {
            //   roomId: roomData.roomUuid,
            //   joinCode: roomData.joinCode,
            // });
            
            setSyncCode(roomData.joinCode);
            setSyncRoomData(roomData);
            // console.log('[동기화] 소켓 생성 완료:', roomData);
          }
        } else {
          // 2시간 이내 소켓이 없음 - 신규 생성
          // console.log('[동기화] 2시간 이내 소켓 없음, 신규 소켓 생성');
          
          // 쿠키에 저장된 room_id가 있으면 이전 소켓 닫기
          if (savedRoomId) {
            try {
              // console.log('[동기화] 만료된 소켓이므로 이전 소켓 닫기:', savedRoomId);
              await syncService.closeSyncRoom(savedRoomId);
              // console.log('[동기화] 이전 소켓 닫기 완료');
            } catch (error) {
              // console.error('[동기화] 이전 소켓 닫기 실패:', error);
              // 닫기 실패해도 계속 진행
            }
          }
          
          // 쿠키 삭제
          deleteSyncRoomCookies();
          
          // 신규 소켓 생성
          const roomData = await syncService.createSyncRoom({
            bookUuid: bookUuid,
            title: '동기화 수업',
          });
          
          // 쿠키에 room_id와 join_code 저장
          setSyncRoomCookies(roomData.roomUuid, roomData.joinCode, 2); // 2시간 유효
          // console.log('[동기화] 쿠키에 저장 완료:', {
          //   roomId: roomData.roomUuid,
          //   joinCode: roomData.joinCode,
          // });
          
          setSyncCode(roomData.joinCode);
          setSyncRoomData(roomData);
          // console.log('[동기화] 소켓 생성 완료:', roomData);
        }
      } catch (error: any) {
        // console.error('[동기화] 소켓 생성 실패:', error);
        setSyncError(error.message || '동기화 소켓 생성에 실패했습니다.');
      } finally {
        setIsSyncLoading(false);
      }
    } else {
      // 학생: 입력 초기화
      setStudentInputCode(['', '', '', '']);
      setIsSyncModalOpen(true);
    }
    onSync?.();
  }, [isProfessor, bookUuid, onSync]);

  // 동기화 토글 클릭 핸들러
  const handleSyncToggleClick = useCallback(() => {
    if (isSyncSocketConnected) {
      setIsSyncDisconnectModalOpen(true);
    } else {
      handleSyncClick();
    }
  }, [isSyncSocketConnected, handleSyncClick]);

  // 동기화 해제 확인 핸들러
  const handleSyncDisconnectConfirm = useCallback(async () => {
    setIsSyncDisconnectModalOpen(false);
    disconnectSyncSocket();
    if (syncRoomData?.roomUuid) {
      try {
        await syncService.closeSyncRoom(syncRoomData.roomUuid);
        // console.log('[동기화] 토글 OFF - 방 닫기 완료:', syncRoomData.roomUuid);
      } catch (error) {
        // console.error('[동기화] 토글 OFF - 방 닫기 실패:', error);
      }
      deleteSyncRoomCookies();
    }
    setSyncRoomData(null);
    setSyncCode('');
  }, [disconnectSyncSocket, syncRoomData?.roomUuid]);

  // 동기화 모달 닫기
  // 주의: 학생이 코드 제출 후 모달을 닫을 때 syncCode를 유지해야 함
  const handleCloseSyncModal = useCallback((clearCode: boolean = false) => {
    setIsSyncModalOpen(false);
    // clearCode가 true이거나, syncRoomData가 없는 경우에만 syncCode 초기화
    // (학생이 연결 성공 후에는 syncCode 유지)
    if (clearCode) {
      setSyncCode('');
    }
    setStudentInputCode(['', '', '', '']);
  }, []);

  // 동기화 코드 다시 생성 (기존 room 닫고 새로 생성)
  const handleRegenerateSyncCode = useCallback(async () => {
    if (!isProfessor || !bookUuid) return;
    setIsSyncLoading(true);
    setSyncError(null);
    try {
      if (syncRoomData?.roomUuid) {
        try {
          await syncService.closeSyncRoom(syncRoomData.roomUuid);
          // console.log('[동기화] 기존 방 닫기 완료:', syncRoomData.roomUuid);
        } catch (error) {
          // console.error('[동기화] 기존 방 닫기 실패:', error);
        }
        deleteSyncRoomCookies();
      }
      const roomData = await syncService.createSyncRoom({
        bookUuid: bookUuid,
        title: '동기화 수업',
      });
      setSyncRoomCookies(roomData.roomUuid, roomData.joinCode, 2);
      setSyncCode(roomData.joinCode);
      setSyncRoomData(roomData);
      // console.log('[동기화] 코드 재생성 완료:', roomData);
    } catch (error: any) {
      // console.error('[동기화] 코드 재생성 실패:', error);
      setSyncError(error.message || '동기화 코드 재생성에 실패했습니다.');
    } finally {
      setIsSyncLoading(false);
    }
  }, [isProfessor, bookUuid, syncRoomData?.roomUuid]);

  // 학생 코드 입력 핸들러
  const handleCodeInputChange = useCallback((index: number, value: string) => {
    const numValue = value.replace(/[^0-9]/g, '');
    if (numValue.length > 1) return;

    const newCode = [...studentInputCode];
    newCode[index] = numValue;
    setStudentInputCode(newCode);

    if (numValue && index < 3) {
      const nextInput = document.getElementById(`sync-code-input-${index + 1}`);
      nextInput?.focus();
    }
  }, [studentInputCode]);

  // 키보드 이벤트 핸들러 (백스페이스로 이전 필드 이동)
  const handleCodeKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !studentInputCode[index] && index > 0) {
      const prevInput = document.getElementById(`sync-code-input-${index - 1}`);
      prevInput?.focus();
    }
  }, [studentInputCode]);

  // 학생 동기화 코드 제출
  const handleStudentSyncSubmit = useCallback(async () => {
    const code = studentInputCode.join('');
    if (code.length !== 4) return;
      
    setIsSyncLoading(true);
    setSyncError(null);
      
    try {
      const resolveData = await syncService.resolveSyncRoom(code);
      if (effectiveBookUid && resolveData.bookUuid && resolveData.bookUuid !== effectiveBookUid) {
        setSyncError('서로 다른 도서는 동기화할 수 없습니다.');
        setStudentInputCode(['', '', '', '']);
        document.getElementById('sync-code-input-0')?.focus();
        return;
      }
      setSyncCode(code);
      setSyncRoomData(resolveData);
      handleCloseSyncModal();
    } catch (error: any) {
      setSyncError('동기화 코드를 다시 확인해주세요.');
      setStudentInputCode(['', '', '', '']);
      document.getElementById('sync-code-input-0')?.focus();
    } finally {
      setIsSyncLoading(false);
    }
  }, [studentInputCode, handleCloseSyncModal, effectiveBookUid]);

  // 4자리 입력 완료 시 자동 제출
  useEffect(() => {
    if (studentInputCode.every(d => d !== '') && !isSyncLoading) {
      handleStudentSyncSubmit();
    }
  }, [studentInputCode, isSyncLoading, handleStudentSyncSubmit]);

  // 페이지 이탈 시 소켓 정상 종료 확인
  useEffect(() => {
    const handleBeforeUnload = () => {
      // 페이지 이탈 시 소켓 정상 종료
      if (isSyncSocketConnected) {
        // console.log('[동기화] 페이지 이탈 감지, 소켓 정상 종료:', {
        //   role: isProfessor ? '교수자' : '학생',
        // });
        disconnectSyncSocket();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isSyncSocketConnected, isProfessor, disconnectSyncSocket]);

  // 학생 코드 입력 완료 여부
  const isStudentCodeComplete = studentInputCode.every(digit => digit !== '');


  // 학생: 동기화 요청 거절
  const handleRejectSync = useCallback(() => {
    // console.log('[Layout] handleRejectSync 시작 - 현재 상태:', { showSyncFloatingButton, pendingPageNo, isStudent });
    setIsSyncRequestModalOpen(false);
    setShowSyncFloatingButton(true);
    
    // Custom Event로 BookViewer에 알림
    window.dispatchEvent(new CustomEvent('syncLocationButtonShow', {
      detail: {
        showSyncLocationButton: true,
        syncPendingPageNo: pendingPageNo,
      }
    }));
    // console.log('[Layout] handleRejectSync 완료 - Custom Event 발생:', { showSyncLocationButton: true, syncPendingPageNo: pendingPageNo });
    // pendingPageNo는 유지 (나중에 이동할 수 있도록)
  }, [pendingPageNo]);

  const contextShowSyncLocationButton = isStudent && showSyncFloatingButton;
  // console.log('[Layout] SyncSocketProvider 렌더링:', {
  //   isStudent,
  //   showSyncFloatingButton,
  //   contextShowSyncLocationButton,
  //   pendingPageNo,
  // });

  // 공유 폴더 수신 상태 초기화 함수
  const clearReceivedSharedFolder = useCallback(() => {
    setReceivedSharedFolder(null);
  }, []);

  // 공유 폴더 수신 시 자동 수락
  useEffect(() => {
    if (!receivedSharedFolder?.folder_uuid) return;
    const folderUuid = receivedSharedFolder.folder_uuid;
    apiClient
      .post(`/viewer/shared-folder/${folderUuid}/accept`, { folder_uuid: folderUuid })
      .then((response) => {
        if (response.data.success) {
          // console.log('[Layout] 공유 폴더 자동 수락 성공:', response.data);
        } else {
          // console.error('[Layout] 공유 폴더 자동 수락 실패:', response.data.error);
        }
      })
      .catch((error) => {
        // console.error('[Layout] 공유 폴더 자동 수락 API 호출 실패:', error);
      });
  }, [receivedSharedFolder?.folder_uuid]);

  return (
<SyncSocketProvider
      isSyncSocketConnected={isSyncSocketConnected}
      sendSyncStart={sendSyncStart}
      sendQuizPublish={sendQuizPublish}
      sendSharedFolderPublish={sendSharedFolderPublish}
      receivedSharedFolder={receivedSharedFolder}
      clearReceivedSharedFolder={clearReceivedSharedFolder}
      showSyncLocationButton={contextShowSyncLocationButton}
      syncPendingPageNo={pendingPageNo}
      onSyncLocationClick={handleFloatingButtonClick}
    >
      <LayoutContainer>
        <Header>
          <HeaderContent>
            <LeftSection>
              <Logo $isDark={isDarkMode} onClick={handleLogoClick}>
                <img src="/images/global/logo.svg" alt="logo" />
                {isBookViewerPage && bookTitle && (
                  <>
                    <BookTitleSeparator />
                    <BookTitleText>{bookTitle}</BookTitleText>
                  </>
                )}
              </Logo>
              {!isBookViewerPage && isAuthenticated && (
                <MenuButtonContainer ref={menuDropdownRef}>
                  <MenuButton onClick={handleMenuToggle} aria-label="메뉴 열기">
                    <img src="/images/book/icon_setting.svg" alt="menu" />
                  </MenuButton>
                  {isMenuDropdownOpen && (
                    <MenuDropdown>
                      <MenuDropdownItem onClick={() => handleMenuClick('profile')}>
                        내 정보
                      </MenuDropdownItem>
                      <MenuDropdownItem onClick={() => handleMenuClick('inquiry')}>
                        문의하기
                      </MenuDropdownItem>
                      <MenuDropdownItem onClick={() => handleMenuClick('logout')}>
                        로그아웃
                      </MenuDropdownItem>
                    </MenuDropdown>
                  )}
                </MenuButtonContainer>
              )}
            </LeftSection>
            
            {isProfessor && isBookViewerPage && (
              <IconGroup $isCentered={isBookViewerPage}>
                <AnimatedIconButton 
                  aria-label="학습기록" 
                  $pop={isLearningRecordsPop}
                  onClick={onOpenLearningRecords}
                  onAnimationEnd={() => setIsLearningRecordsPop(false)}
                  title="학습기록 보기"
                >
                  <img src="/images/icons/header/history.svg" alt="학습기록" width={24} height={24} />
                </AnimatedIconButton>
                <IconButton 
                  aria-label="첨부파일" 
                  onClick={handleOpenAttachmentModal}
                  title="첨부파일 보기"
                >
                  <img src="/images/icons/header/file-download.svg" alt="첨부파일" width={24} height={24} />
                </IconButton>
                <IconButton aria-label="폰트" onClick={handleOpenFontModal} title="폰트 설정">
                  <img src="/images/icons/header/font.svg" alt="폰트" width={24} height={24} />
                </IconButton>
                <IconButton 
                  aria-label="음성" 
                  onClick={onVoiceRecord}
                  title="음성 녹음"
                >
                  <img src="/images/icons/header/microphone.svg" alt="음성" width={24} height={24} />
                </IconButton>
                <AnimatedIconButton 
                  aria-label="뿍챗" 
                  $pop={isAIPanelPop}
                  onClick={onOpenAIPanel}
                  onAnimationEnd={() => setIsAIPanelPop(false)}
                  title="뿍챗 열기"
                >
                  <img src="/images/icons/header/chat.svg" alt="뿍챗" width={24} height={24} />
                </AnimatedIconButton>
                {/*
                <IconButton
                  aria-label="퀴즈 생성"
                  onClick={handleQuizCreateClick}
                  onMouseDown={handleQuizCreateMouseDown}
                  title="퀴즈 생성"
                >
                  <img src="/images/icons/header/quiz.svg" alt="퀴즈 생성" width={24} height={24} />
                </IconButton>
                */}
                <MenuButtonContainer ref={menuDropdownRef}>
                  <MenuButton onClick={handleMenuToggle} aria-label="메뉴 열기">
                    <img src="/images/book/icon_setting.svg" alt="menu" />
                  </MenuButton>
                  {isMenuDropdownOpen && (
                    <MenuDropdown $alignRight>
                      <MenuDropdownItem onClick={() => handleMenuClick('profile')}>
                        내 정보
                      </MenuDropdownItem>
                      <MenuDropdownItem onClick={() => handleMenuClick('inquiry')}>
                        문의하기
                      </MenuDropdownItem>
                      <MenuDropdownItem onClick={() => handleMenuClick('logout')}>
                        로그아웃
                      </MenuDropdownItem>
                    </MenuDropdown>
                  )}
                </MenuButtonContainer>
              </IconGroup>
            )}
            {isStudent && isBookViewerPage && (
              <IconGroup $isCentered={isBookViewerPage}>
                <AnimatedIconButton 
                  aria-label="학습기록" 
                  $pop={isLearningRecordsPop}
                  onClick={onOpenLearningRecords}
                  onAnimationEnd={() => setIsLearningRecordsPop(false)}
                  title="학습기록 보기"
                >
                  <img src="/images/icons/header/history.svg" alt="학습기록" width={24} height={24} />
                </AnimatedIconButton>
                <IconButton 
                  aria-label="첨부파일" 
                  onClick={handleOpenAttachmentModal}
                  title="첨부파일 보기"
                >
                  <img src="/images/icons/header/file-download.svg" alt="첨부파일" width={24} height={24} />
                </IconButton>
                <IconButton aria-label="폰트" onClick={handleOpenFontModal} title="폰트 설정">
                  <img src="/images/icons/header/font.svg" alt="폰트" width={24} height={24} />
                </IconButton>
                <AnimatedIconButton 
                  aria-label="뿍챗" 
                  $pop={isAIPanelPop}
                  onClick={onOpenAIPanel}
                  onAnimationEnd={() => setIsAIPanelPop(false)}
                  title="뿍챗 열기"
                >
                  <img src="/images/icons/header/chat.svg" alt="뿍챗" width={24} height={24} />
                </AnimatedIconButton>
                <IconButton
                  aria-label="교수의 노트"
                  onClick={() => setIsProfessorNotesModalOpen(true)}
                  title="교수의 노트"
                >
                  <img src="/images/icons/header/professor-note.svg" alt="교수의 노트" width={24} height={24} />
                </IconButton>
                {/*
                <IconButton
                  aria-label="퀴즈 생성"
                  onClick={handleQuizCreateClick}
                  onMouseDown={handleQuizCreateMouseDown}
                  title="퀴즈 생성"
                >
                  <img src="/images/icons/header/quiz.svg" alt="퀴즈 생성" width={24} height={24} />
                </IconButton>
                */}
                <MenuButtonContainer ref={menuDropdownRef}>
                  <MenuButton onClick={handleMenuToggle} aria-label="메뉴 열기">
                    <img src="/images/book/icon_setting.svg" alt="menu" />
                  </MenuButton>
                  {isMenuDropdownOpen && (
                    <MenuDropdown $alignRight>
                      <MenuDropdownItem onClick={() => handleMenuClick('profile')}>
                        내 정보
                      </MenuDropdownItem>
                      <MenuDropdownItem onClick={() => handleMenuClick('inquiry')}>
                        문의하기
                      </MenuDropdownItem>
                      <MenuDropdownItem onClick={() => handleMenuClick('logout')}>
                        로그아웃
                      </MenuDropdownItem>
                    </MenuDropdown>
                  )}
                </MenuButtonContainer>
              </IconGroup>
            )}
            
            <UserInfo>
              {(isProfessor || isStudent) && isBookViewerPage && (
                <SyncButton
                  $isOn={isSyncSocketConnected}
                  onClick={handleSyncToggleClick}
                  aria-label="동기화"
                  title={isProfessor ? '학생들과 화면 동기화' : '교수님 화면으로 동기화'}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M6.8 0.774194C6.8 0.345968 6.4425 0 6 0C5.5575 0 5.2 0.345968 5.2 0.774194V6.19355C5.2 6.62177 5.5575 6.96774 6 6.96774C6.4425 6.96774 6.8 6.62177 6.8 6.19355V0.774194ZM3.1875 2.91774C3.5275 2.64436 3.5725 2.15565 3.29 1.82661C3.0075 1.49758 2.5025 1.45403 2.1625 1.72742C0.8425 2.79194 0 4.39839 0 6.19355C0 9.39919 2.6875 12 6 12C9.3125 12 12 9.39919 12 6.19355C12 4.39839 11.155 2.79194 9.835 1.72742C9.495 1.45403 8.99 1.5 8.7075 1.82661C8.425 2.15323 8.4725 2.64436 8.81 2.91774C9.7825 3.69919 10.3975 4.87742 10.3975 6.19355C10.3975 8.54516 8.4275 10.4516 5.9975 10.4516C3.5675 10.4516 1.5975 8.54516 1.5975 6.19355C1.5975 4.87742 2.215 3.69919 3.185 2.91774" fill="#2D2D2D"/>
                  </svg>
                  {isSyncSocketConnected ? 'Live on' : 'Live off'}
                </SyncButton>
              )}
              <DarkModeToggle isDark={isDarkMode} onToggle={toggleDarkMode} />
              {onToggleAnnotations && (
                <AnnotationsToggle isHidden={hideAnnotations} onToggle={onToggleAnnotations} />
              )}
              {!isAuthenticated && (
                <LogoutButton onClick={() => window.location.href = '/login'}>
                  로그인
                </LogoutButton>
              )}
            </UserInfo>
          </HeaderContent>
        </Header>
        
        {/* 탭 바 - PDF 파일이 열려있을 때만 표시 */}
        {effectiveBookUid && pdfTabs.length > 0 && (
          <TabBar>
            {/* 기본 책 탭 - PDF 파일이 열려있을 때만 표시 */}
            <Tab 
              $isActive={activeTabId === BOOK_TAB_ID}
              onClick={() => handleSelectTab(BOOK_TAB_ID)}
            >
              원문보기
            </Tab>
            
            {/* PDF 탭들 */}
            {pdfTabs.map(tab => (
              <Tab 
                key={tab.id}
                $isActive={activeTabId === tab.id}
                onClick={() => handleSelectTab(tab.id)}
              >
                {tab.fileName.length > 15 ? `${tab.fileName.substring(0, 15)}...` : tab.fileName}
                <TabCloseButton onClick={(e) => handleCloseTab(tab.id, e)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </TabCloseButton>
              </Tab>
            ))}
            
            {/* 탭 추가 버튼 (첨부파일 모달 열기) */}
            <AddTabButton onClick={handleOpenAttachmentModal} title="첨부파일에서 PDF 열기">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </AddTabButton>
          </TabBar>
        )}
        
        <Main>
          {/* 활성 탭에 따라 콘텐츠 표시 */}
          {activeTabId === BOOK_TAB_ID ? (
            children
          ) : activePdfTab ? (
            <PdfViewer
              bookUid={effectiveBookUid}
              fileUuid={activePdfTab.fileUuid}
              fileName={activePdfTab.fileName}
            />
          ) : (
            children
          )}
        </Main>

        {/* 교수 퀵뷰 플로팅 버튼 */}
        {isProfessor && isBookViewerPage && isSyncSocketConnected && (
          <QuickViewFloatingButton
            onClick={handleViewClick}
            aria-label="퀵뷰"
            title="현재 페이지를 학생들에게 전송"
          >
            {/* <img src="/images/icons/user/book/quick_view.svg" alt="퀵뷰" /> */}
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M10 4.16663C10 2.98829 10 2.39913 10.3667 2.03329C10.7317 1.66663 11.3208 1.66663 12.5 1.66663H13.3333C14.5117 1.66663 15.1008 1.66663 15.4667 2.03329C15.8333 2.39829 15.8333 2.98746 15.8333 4.16663C15.8333 5.34579 15.8333 5.93413 15.4667 6.29996C15.1017 6.66663 14.5125 6.66663 13.3333 6.66663H12.5C11.3217 6.66663 10.7325 6.66663 10.3667 6.29996C10 5.93496 10 5.34579 10 4.16663ZM15.8333 3.12496L17.1792 2.56413C17.7017 2.34663 17.9625 2.23746 18.1475 2.36163C18.3333 2.48496 18.3333 2.76829 18.3333 3.33329V4.99996C18.3333 5.56496 18.3333 5.84829 18.1475 5.97163C17.9625 6.09496 17.7017 5.98663 17.1792 5.76913L15.8333 5.20829V3.12496Z" stroke="white" strokeWidth="1.5"/>
              <path d="M13.3334 18.3333H6.66669M10 14.1666V18.3333M18.3334 8.33329V9.16663C18.3334 11.5233 18.3334 12.7025 17.6009 13.4341C16.8692 14.1666 15.69 14.1666 13.3334 14.1666H6.66669C4.31002 14.1666 3.13085 14.1666 2.39919 13.4341C1.66669 12.7025 1.66669 11.5233 1.66669 9.16663V8.33329M7.91669 1.66663C5.17752 1.66663 3.80752 1.66663 2.88502 2.42329C2.71639 2.5617 2.56176 2.71633 2.42335 2.88496C2.00252 3.39829 1.81502 4.04996 1.73252 4.99996M18.3334 10.8333H13.3334M1.66669 10.8333H10" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </QuickViewFloatingButton>
        )}

        {/* 첨부파일 모달 */}
        {effectiveBookUid && (
          <AttachmentModal
            isOpen={isAttachmentModalOpen}
            onClose={handleCloseAttachmentModal}
            bookUid={effectiveBookUid}
            onOpenPdf={handleOpenPdfFromModal}
          />
        )}

        {/* 교수의 노트 모달 (학생 전용) */}
        {isStudent && (
          <ProfessorNotesModal
            isOpen={isProfessorNotesModalOpen}
            onClose={() => setIsProfessorNotesModalOpen(false)}
            bookUid={effectiveBookUid}
          />
        )}

        {/* 동기화 해제 확인 모달 */}
        {isSyncDisconnectModalOpen && (
          <SyncDisconnectModalOverlay onClick={() => setIsSyncDisconnectModalOpen(false)}>
            <SyncDisconnectModalContent onClick={(e) => e.stopPropagation()}>
              <SyncDisconnectModalClose onClick={() => setIsSyncDisconnectModalOpen(false)}>
                ×
              </SyncDisconnectModalClose>
              <SyncDisconnectModalTitle>동기화가 종료됩니다.</SyncDisconnectModalTitle>
              <SyncDisconnectModalCodeLabel>현재 연결중인 동기화 번호입니다.</SyncDisconnectModalCodeLabel>
              <SyncDisconnectModalCodeGroup>
                {[0, 1, 2, 3].map((index) => (
                  <SyncDisconnectModalCodeBox key={index}>
                    {(syncCode || (syncRoomData && 'joinCode' in syncRoomData ? syncRoomData.joinCode : '') || '')[index] ?? ''}
                  </SyncDisconnectModalCodeBox>
                ))}
              </SyncDisconnectModalCodeGroup>
              <SyncDisconnectModalDesc>
                확인을 누르시면, {isProfessor ? '학습자와의' : '교수와의'} 연결이 끊어집니다.
              </SyncDisconnectModalDesc>
              <SyncDisconnectModalButtons>
                <SyncDisconnectModalBtn onClick={() => setIsSyncDisconnectModalOpen(false)}>
                  취소
                </SyncDisconnectModalBtn>
                <SyncDisconnectModalBtn $primary onClick={handleSyncDisconnectConfirm}>
                  확인
                </SyncDisconnectModalBtn>
              </SyncDisconnectModalButtons>
            </SyncDisconnectModalContent>
          </SyncDisconnectModalOverlay>
        )}

        {/* 동기화 모달 */}
        <Modal
          isOpen={isSyncModalOpen}
          onClose={() => handleCloseSyncModal(true)}
          title={isProfessor ? '동기화 코드를 생성했습니다.' : '동기화 코드를 입력해주세요.'}
          subtitle={isProfessor ? "학습자에게 공유해주세요." : '교수자에게 코드를 확인해주세요.'}
          width={410}
          position={{ top: '72px', right: '20px' }}
          showCloseButton={true}
          showOverlay ={false}
        >
          {isProfessor ? (
            // 교수자: 동기화 코드 표시
            <>
              {isSyncLoading ? (
                <SyncLoadingSpinner />
              ) : syncError ? (
                <>
                  <SyncErrorMessage>{syncError}</SyncErrorMessage>
                </>
              ) : (
                <>
                  <SyncCodeBoxGroup>
                    {[0, 1, 2, 3].map((index) => (
                      <SyncCodeBox key={index}>
                        {syncCode[index] ?? ''}
                      </SyncCodeBox>
                    ))}
                  </SyncCodeBoxGroup>
                  <SyncDescription>
                    코드가 생성이 안됐나요?{' '}
                    <SyncRegenerateLink
                      onClick={handleRegenerateSyncCode}
                      disabled={isSyncLoading}
                    >
                      다시 생성하기
                    </SyncRegenerateLink>
                  </SyncDescription>
                  <SyncModalFooter>
                    <SyncModalButton onClick={() => handleCloseSyncModal(true)}>
                      취소
                    </SyncModalButton>
                    <SyncModalButton $primary onClick={() => handleCloseSyncModal(false)}>
                      확인
                    </SyncModalButton>
                  </SyncModalFooter>
                </>
              )}
            </>
          ) : (
            // 학생: 동기화 코드 입력
            <SyncCodeInputContainer>
              <SyncCodeInputGroup>
                {[0, 1, 2, 3].map((index) => (
                  <SyncCodeInput
                    key={index}
                    id={`sync-code-input-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={studentInputCode[index]}
                    onChange={(e) => handleCodeInputChange(index, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(index, e)}
                    placeholder="0"
                    autoFocus={index === 0}
                    disabled={isSyncLoading}
                  />
                ))}
              </SyncCodeInputGroup>
              {syncError && <SyncErrorMessage>{syncError}</SyncErrorMessage>}
            </SyncCodeInputContainer>
          )}
        </Modal>

        {/* 폰트 설정 모달 */}
        <Modal
          isOpen={isFontModalOpen}
          onClose={handleCloseFontModal}
          title="폰트 설정"
          width={380}
          position={{ top: '82px', left: '50%' }}
          containerStyle={{ transform: 'translateX(-50%)', maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}
          showCloseButton={true}
          zIndex={100000}
          showOverlay={false}
        >
          <FontSettingsContainer>
            <FontSelect
              value={draftFontSettings.fontFamily}
              onChange={(e) => handleDraftFontSettingChange('fontFamily', e.target.value)}
            >
              <option value="inherit">기본 폰트</option>
              <option value="Noto Sans KR">Noto Sans KR</option>
              <option value="Nanum Gothic">나눔고딕</option>
              <option value="Nanum Myeongjo">나눔명조</option>
              {devicePlatform === 'ios' && (
                <>
                  <option value="Apple SD Gothic Neo">Apple SD Gothic Neo</option>
                  <option value="SF Pro, -apple-system">SF Pro (San Francisco)</option>
                </>
              )}
              {devicePlatform === 'android' && (
                <>
                  <option value="One UI Sans, SamsungSans, Samsung One">One UI Sans</option>
                  <option value="SamsungSans, Samsung One, One UI Sans">Samsung Sans</option>
                </>
              )}
              {devicePlatform === 'desktop' && (
                <>
                  <option value="Malgun Gothic">맑은 고딕</option>
                  <option value="Gulim">굴림</option>
                  <option value="Dotum">돋움</option>
                  <option value="Batang">바탕</option>
                  <option value="Gungsuh">궁서</option>
                </>
              )}
            </FontSelect>

            <SliderRow>
              <SliderRowLabel>크기</SliderRowLabel>
              <SliderIndicator $fontSize={12}>가</SliderIndicator>
              <SliderColumn>
                <SliderWrapper>
                  <SliderTrack />
                  <SliderFill $percent={((localSliderValues.fontSize ?? draftFontSettings.fontSize) - FONT_MODAL_RANGES.fontSize.min) / (FONT_MODAL_RANGES.fontSize.max - FONT_MODAL_RANGES.fontSize.min) * 100} />
                  <FontSettingSlider
                    type="range"
                    min={FONT_MODAL_RANGES.fontSize.min}
                    max={FONT_MODAL_RANGES.fontSize.max}
                    step="1"
                    value={localSliderValues.fontSize ?? draftFontSettings.fontSize}
                    onChange={(e) => handleSliderChange('fontSize', Number(e.target.value), [...FONT_MODAL_SNAP_POINTS.fontSize])}
                    onMouseDown={handleSliderMouseDown}
                    onMouseUp={createSliderMouseUpHandler('fontSize', [...FONT_MODAL_SNAP_POINTS.fontSize])}
                    onTouchStart={handleSliderMouseDown}
                    onTouchEnd={createSliderMouseUpHandler('fontSize', [...FONT_MODAL_SNAP_POINTS.fontSize])}
                  />
                </SliderWrapper>
                <SnapDotsRow>
                  {FONT_MODAL_SNAP_POINTS.fontSize.map((_, idx) => (
                    <SnapDot key={idx} />
                  ))}
                </SnapDotsRow>
              </SliderColumn>
              <SliderIndicator $fontSize={24}>가</SliderIndicator>
            </SliderRow>

            <SliderRow>
              <SliderRowLabel>굵기</SliderRowLabel>
              <SliderIndicator $fontWeight={300}>B</SliderIndicator>
              <SliderColumn>
                <SliderWrapper>
                  <SliderTrack />
                  <SliderFill $percent={((localSliderValues.fontWeight ?? fontWeightToValue(draftFontSettings.fontWeight)) - FONT_MODAL_RANGES.fontWeight.min) / (FONT_MODAL_RANGES.fontWeight.max - FONT_MODAL_RANGES.fontWeight.min) * 100} />
                  <FontSettingSlider
                    type="range"
                    min={FONT_MODAL_RANGES.fontWeight.min}
                    max={FONT_MODAL_RANGES.fontWeight.max}
                    step="0.1"
                    value={localSliderValues.fontWeight ?? fontWeightToValue(draftFontSettings.fontWeight)}
                    onChange={(e) => handleSliderChange('fontWeight', Number(e.target.value), [...FONT_MODAL_SNAP_POINTS.fontWeight])}
                    onMouseDown={handleSliderMouseDown}
                    onMouseUp={createSliderMouseUpHandler('fontWeight', [...FONT_MODAL_SNAP_POINTS.fontWeight])}
                    onTouchStart={handleSliderMouseDown}
                    onTouchEnd={createSliderMouseUpHandler('fontWeight', [...FONT_MODAL_SNAP_POINTS.fontWeight])}
                  />
                </SliderWrapper>
                <SnapDotsRow>
                  {FONT_MODAL_SNAP_POINTS.fontWeight.map((_, idx) => (
                    <SnapDot key={idx} />
                  ))}
                </SnapDotsRow>
              </SliderColumn>
              <SliderIndicator $fontWeight={700}>B</SliderIndicator>
            </SliderRow>

            <SliderRow>
              <SliderRowLabel>자간</SliderRowLabel>
              <SliderIndicator>-10</SliderIndicator>
              <SliderColumn>
                <SliderWrapper>
                  <SliderTrack />
                  <SliderFill $percent={((localSliderValues.letterSpacing ?? draftFontSettings.letterSpacing) - FONT_MODAL_RANGES.letterSpacing.min) / (FONT_MODAL_RANGES.letterSpacing.max - FONT_MODAL_RANGES.letterSpacing.min) * 100} />
                  <FontSettingSlider
                    type="range"
                    min={FONT_MODAL_RANGES.letterSpacing.min}
                    max={FONT_MODAL_RANGES.letterSpacing.max}
                    step="0.1"
                    value={localSliderValues.letterSpacing ?? draftFontSettings.letterSpacing}
                    onChange={(e) => handleSliderChange('letterSpacing', Number(e.target.value), [...FONT_MODAL_SNAP_POINTS.letterSpacing])}
                    onMouseDown={handleSliderMouseDown}
                    onMouseUp={createSliderMouseUpHandler('letterSpacing', [...FONT_MODAL_SNAP_POINTS.letterSpacing])}
                    onTouchStart={handleSliderMouseDown}
                    onTouchEnd={createSliderMouseUpHandler('letterSpacing', [...FONT_MODAL_SNAP_POINTS.letterSpacing])}
                  />
                </SliderWrapper>
                <SnapDotsRow>
                  {FONT_MODAL_SNAP_POINTS.letterSpacing.map((_, idx) => (
                    <SnapDot key={idx} />
                  ))}
                </SnapDotsRow>
              </SliderColumn>
              <SliderIndicator>10</SliderIndicator>
            </SliderRow>

            <SliderRow>
              <SliderRowLabel>행간</SliderRowLabel>
              <SliderIndicator>-20</SliderIndicator>
              <SliderColumn>
                <SliderWrapper>
                  <SliderTrack />
                  <SliderFill $percent={((localSliderValues.lineHeight ?? lineHeightToValue(draftFontSettings.lineHeight)) - FONT_MODAL_RANGES.lineHeight.min) / (FONT_MODAL_RANGES.lineHeight.max - FONT_MODAL_RANGES.lineHeight.min) * 100} />
                  <FontSettingSlider
                    type="range"
                    min={FONT_MODAL_RANGES.lineHeight.min}
                    max={FONT_MODAL_RANGES.lineHeight.max}
                    step="0.1"
                    value={localSliderValues.lineHeight ?? lineHeightToValue(draftFontSettings.lineHeight)}
                    onChange={(e) => handleSliderChange('lineHeight', Number(e.target.value), [...FONT_MODAL_SNAP_POINTS.lineHeight])}
                    onMouseDown={handleSliderMouseDown}
                    onMouseUp={createSliderMouseUpHandler('lineHeight', [...FONT_MODAL_SNAP_POINTS.lineHeight])}
                    onTouchStart={handleSliderMouseDown}
                    onTouchEnd={createSliderMouseUpHandler('lineHeight', [...FONT_MODAL_SNAP_POINTS.lineHeight])}
                  />
                </SliderWrapper>
                <SnapDotsRow>
                  {FONT_MODAL_SNAP_POINTS.lineHeight.map((_, idx) => (
                    <SnapDot key={idx} />
                  ))}
                </SnapDotsRow>
              </SliderColumn>
              <SliderIndicator>20</SliderIndicator>
            </SliderRow>

            <FontSettingLabel>페이지 보기</FontSettingLabel>
            <PageViewToggleGroup>
              <PageViewToggleBtn
                type="button"
                $active={pagesPerView === 1}
                onClick={() => handlePagesPerViewChange(1)}
              >
                1쪽 보기
              </PageViewToggleBtn>
              <PageViewToggleBtn
                type="button"
                $active={pagesPerView === 2}
                onClick={() => handlePagesPerViewChange(2)}
              >
                2쪽 보기
              </PageViewToggleBtn>
            </PageViewToggleGroup>

            <FontSettingLabel>미리보기</FontSettingLabel>
            <FontPreview
              $fontFamily={draftFontSettings.fontFamily}
              $fontSize={localSliderValues.fontSize ?? draftFontSettings.fontSize}
              $fontWeight={valueToFontWeight(localSliderValues.fontWeight ?? fontWeightToValue(draftFontSettings.fontWeight))}
              $letterSpacing={localSliderValues.letterSpacing ?? draftFontSettings.letterSpacing}
              $lineHeight={valueToLineHeight(localSliderValues.lineHeight ?? lineHeightToValue(draftFontSettings.lineHeight))}
            >
              가나다라마바사아자차카타파하. The quick brown fox jumps over the lazy dog. 0123456789
            </FontPreview>

            <FontSettingsButtonGroup>
              <FontSettingsButton onClick={handleDraftReset}>
                초기화
              </FontSettingsButton>
              <FontSettingsButton $variant="primary" onClick={handleSaveFontSettings}>
                적용
              </FontSettingsButton>
            </FontSettingsButtonGroup>
          </FontSettingsContainer>
        </Modal>

        {/* 학생용: 동기화 요청 모달 */}
        {isStudent && (
          <Modal
            isOpen={isSyncRequestModalOpen}
            onClose={handleRejectSync}
            width={380}
            title="교수자의 위치 동기화"
            subtitle="교수님이 페이지 이동을 요청했습니다"
          >
            <SyncRequestContent>
              <SyncRequestPageNo style={{ padding: '16px' }}>
                {pendingPageNo !== null ? `${pendingPageNo} 페이지` : ''}
              </SyncRequestPageNo>
              <SyncRequestButtonGroup style={{ width: '100%', flexDirection: 'row' }}>
                <SyncRequestButton $variant="reject" onClick={handleRejectSync} style={{ width: 'calc(50% - 6px)' }}>
                  나중에
                </SyncRequestButton>
                <SyncRequestButton $variant="accept" onClick={handleAcceptSync} style={{ width: 'calc(50% - 5px)' }}>
                  이동하기
                </SyncRequestButton>
              </SyncRequestButtonGroup>
            </SyncRequestContent>
          </Modal>
        )}


        {/* 학생용: 퀴즈 수신 모달 (한 문제씩 풀기) */}
        {isStudent && quizQueue.length > 0 && (
          <Modal
            isOpen={isQuizReceivedModalOpen}
            showOverlay ={false}
            onClose={() => {
              setIsQuizReceivedModalOpen(false);
              setQuizQueue([]);
              setCurrentQuizIndex(0);
              setCurrentQuizAnswer(null);
              setShowCurrentQuizResult(false);
            }}
            showCloseButton
            title={
              showCurrentQuizResult && currentQuizIndex < quizQueue.length
                ? (() => {
                    const q = quizQueue[currentQuizIndex];
                    const correct = (q?.correct_answer ?? q?.answer ?? '') as 'O' | 'X';
                    const isCorrect = currentQuizAnswer === correct;
                    return isCorrect ? '정답입니다.' : '오답입니다';
                  })()
                : !showCurrentQuizResult
                  ? `퀴즈 풀기 (${currentQuizIndex + 1}/${quizQueue.length})`
                  : '정답 확인'
            }
          >
            <SyncRequestContent>
              {!showCurrentQuizResult ? (
                <>
                  {currentQuizIndex < quizQueue.length && (
                    <>
                      <SyncRequestTitle >
                        Q{currentQuizIndex + 1}. {quizQueue[currentQuizIndex].question}
                      </SyncRequestTitle>
                      <div style={{ width: '100%'}}>
                        <QuizAnswer >
                          <QuizAnswerButton
                            $variant="yes"
                            $active={currentQuizAnswer === 'O'}
                            onClick={() => setCurrentQuizAnswer('O')}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="42" height="44" viewBox="0 0 42 44" fill="none">
                              <path d="M0 21.1968C0 9.0432 7.3728 0 20.9664 0C34.56 0 41.9328 9.0432 41.9328 21.1968C41.9328 32.8896 35.136 43.4304 20.9664 43.4304C6.1056 43.4304 0 33.7536 0 21.1968ZM9.4464 21.1968C9.4464 29.1456 12.096 35.8272 20.9664 35.8272C29.5488 35.8272 32.4864 29.1456 32.4864 21.1968C32.4864 13.248 28.6848 7.6032 20.9664 7.6032C13.248 7.6032 9.4464 13.248 9.4464 21.1968Z" fill="#2078DC"/>
                            </svg>
                            <span>그렇다</span>
                          </QuizAnswerButton>
                          <QuizAnswerButton
                            $variant="no"
                            $active={currentQuizAnswer === 'X'}
                            onClick={() => setCurrentQuizAnswer('X')}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42" fill="none">
                              <path d="M1.59789 34.019L14.0905 20.0895L2.68737 7.04799C1.96105 6.21555 1.45263 5.32761 1.45263 4.27319C1.45263 1.88686 3.99474 0 7.40842 0C10.2411 0 11.6211 1.27641 12.3474 2.21984L20.9905 13.5965L30.2147 2.21984C31.4495 0.721448 32.4663 0 35.0811 0C37.9863 0 40.8189 1.22091 40.8189 4.1067C40.8189 4.88365 40.3105 5.93807 39.7295 6.54853L27.6 20.0895L39.4389 33.6861C40.6737 35.1289 41.4 35.8504 41.4 36.9048C41.4 39.8461 38.64 41.4 35.2989 41.4C33.3379 41.4 31.6674 41.067 30.2874 39.2911L20.7 27.0265L10.3863 39.6241C9.36947 40.9005 7.55368 41.4 5.73789 41.4C2.25158 41.4 0 39.7351 0 37.5153C0 36.5718 0.217895 35.5729 1.59789 34.019Z" fill="#D94B4B"/>
                            </svg>
                            <span>아니다</span>
                          </QuizAnswerButton>
                        </QuizAnswer>
                      </div>
                      <SyncRequestButtonGroup>
                        <SyncRequestButton 
                          $variant="accept" 
                          onClick={async () => {
                            if (currentQuizAnswer !== null) {
                              const currentQuiz = quizQueue[currentQuizIndex];
                              if (currentQuiz.delivery_uuid && currentQuiz.viewer_uuid) {
                                try {
                                  await quizImageService.markQuizSolved(
                                    currentQuiz.viewer_uuid,
                                    currentQuiz.delivery_uuid,
                                    currentQuizAnswer
                                  );
                                } catch (error) {
                                  // console.error('[Layout] 퀴즈 solved API 실패:', error);
                                }
                              }
                              setShowCurrentQuizResult(true);
                            } else {
                              alert('답변을 선택해주세요.');
                            }
                          }}
                          disabled={currentQuizAnswer === null}
                          style={{ width: '100%' }}
                        >
                          정답확인
                        </SyncRequestButton>
                        <button
                          onClick={() => {
                            setCurrentQuizIndex(prev => prev + 1);
                            setCurrentQuizAnswer(null);
                            setShowCurrentQuizResult(false);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#6b7280',
                            textDecoration: 'underline',
                          }}
                        >
                          건너뛰기
                        </button>
                      </SyncRequestButtonGroup>
                    </>
                  )}
                </>
              ) : (
                <>
                  {currentQuizIndex < quizQueue.length && (() => {
                    const currentQuiz = quizQueue[currentQuizIndex];
                    const correctAnswer = (currentQuiz.correct_answer ?? currentQuiz.answer ?? '') as 'O' | 'X';
                    const hintText = currentQuiz.hint ?? currentQuiz.explanation ?? '';
                    return (
                      <>
                        {hintText && (
                          <SyncRequestTitle>
                            {hintText}
                          </SyncRequestTitle>
                        )}
                        <div style={{ width: '100%' }}>
                          <QuizAnswer>
                            <QuizAnswerButton
                              $variant="yes"
                              $active={correctAnswer === 'O'}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="42" height="44" viewBox="0 0 42 44" fill="none">
                                <path d="M0 21.1968C0 9.0432 7.3728 0 20.9664 0C34.56 0 41.9328 9.0432 41.9328 21.1968C41.9328 32.8896 35.136 43.4304 20.9664 43.4304C6.1056 43.4304 0 33.7536 0 21.1968ZM9.4464 21.1968C9.4464 29.1456 12.096 35.8272 20.9664 35.8272C29.5488 35.8272 32.4864 29.1456 32.4864 21.1968C32.4864 13.248 28.6848 7.6032 20.9664 7.6032C13.248 7.6032 9.4464 13.248 9.4464 21.1968Z" fill="#2078DC"/>
                              </svg>
                              <span>그렇다</span>
                              {correctAnswer === 'O' && <strong>정답</strong>}
                            </QuizAnswerButton>
                            <QuizAnswerButton
                              $variant="no"
                              $active={correctAnswer === 'X'}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42" fill="none">
                                <path d="M1.59789 34.019L14.0905 20.0895L2.68737 7.04799C1.96105 6.21555 1.45263 5.32761 1.45263 4.27319C1.45263 1.88686 3.99474 0 7.40842 0C10.2411 0 11.6211 1.27641 12.3474 2.21984L20.9905 13.5965L30.2147 2.21984C31.4495 0.721448 32.4663 0 35.0811 0C37.9863 0 40.8189 1.22091 40.8189 4.1067C40.8189 4.88365 40.3105 5.93807 39.7295 6.54853L27.6 20.0895L39.4389 33.6861C40.6737 35.1289 41.4 35.8504 41.4 36.9048C41.4 39.8461 38.64 41.4 35.2989 41.4C33.3379 41.4 31.6674 41.067 30.2874 39.2911L20.7 27.0265L10.3863 39.6241C9.36947 40.9005 7.55368 41.4 5.73789 41.4C2.25158 41.4 0 39.7351 0 37.5153C0 36.5718 0.217895 35.5729 1.59789 34.019Z" fill="#D94B4B"/>
                              </svg>
                              <span>아니다</span>
                              {correctAnswer === 'X' && <strong>정답</strong>}
                            </QuizAnswerButton>
                          </QuizAnswer>
                        </div>
                        <SyncRequestButtonGroup>
                          <SyncRequestButton 
                            $variant="accept" 
                            onClick={() => {
                              setCurrentQuizIndex(prev => prev + 1);
                              setCurrentQuizAnswer(null);
                              setShowCurrentQuizResult(false);
                            }}
                          >
                            {currentQuizIndex < quizQueue.length - 1 ? '다음 문제' : '완료'}
                          </SyncRequestButton>
                        </SyncRequestButtonGroup>
                      </>
                    );
                  })()}
                </>
              )}
            </SyncRequestContent>
          </Modal>
        )}

        {/* 공유 폴더 수신 토스트 알림 */}
        <SharedFolderToast $isVisible={showSharedFolderToast}>
          <SharedFolderToastHeader>
            <SharedFolderToastIcon>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
            </SharedFolderToastIcon>
            <SharedFolderToastContent>
              <SharedFolderToastTitle>공유 받았습니다</SharedFolderToastTitle>
              <SharedFolderToastMessage>
                {receivedSharedFolder?.title || '폴더'}
              </SharedFolderToastMessage>
            </SharedFolderToastContent>
            <SharedFolderToastClose onClick={() => setShowSharedFolderToast(false)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </SharedFolderToastClose>
          </SharedFolderToastHeader>
        </SharedFolderToast>
      </LayoutContainer>
    </SyncSocketProvider>
  );
};

export default Layout;
